import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Collapse,
  Typography,
  Tag,
  Input,
  Layout,
  Tooltip,
  Skeleton,
  Popconfirm,
} from "antd";
import {
  DownOutlined,
  RightOutlined,
  CheckOutlined,
  SearchOutlined,
  StarOutlined,
  StarFilled,
  CloseOutlined,
} from "@ant-design/icons";
import "./AttributeList.css";
import { useSelector, useDispatch } from "react-redux";
import {
  setWidgetTotalRow,
  setX_attribute,
} from "../../../../redux/actions/WidgetMaker/GraphConfig";
import {
  setChartAttributesFieldConfig,
  setIsshowSideBar,
} from "../../../../redux/actions/WidgetMaker/WidgetMaker";
import toggleArrow from "../../../../assets/createWidget/toggle_arrow.svg";
import toggleArrowWhite from "../../../../assets/WhiteIcons/toggle_arrow_white.svg";
import { Header } from "antd/lib/layout/layout";
import { v4 as uuid } from "uuid";
import { setDataSource } from "../../../../redux/actions/WidgetMaker/WidgetLibrary";
import { callNotification } from "../../../../redux/constants/index";
import { setFavouritesAttributesAPI } from "../../../../redux/actions/WidgetMaker/WidgetAPI";

const { Panel } = Collapse;
const { Text } = Typography;
const { Content } = Layout;

// Constants
const SEARCH_DEBOUNCE_DELAY = 300;
const SCROLL_ANIMATION_DELAY = 600;
const HIGHLIGHT_DURATION = 1500;
const RESIZE_DEBOUNCE_DELAY = 100;
const MIN_HEIGHT = 630;
const VIEWPORT_HEIGHT_MULTIPLIER = 0.9;
const MAX_HEIGHT_LIMIT = 1200;

const DEFAULT_AGGREGATION = "COUNT";
const DEFAULT_SORT_ORDER = "desc";
const DEFAULT_LIMIT = 10;
const DEFAULT_FILTER_LIMIT = 30;

const AttributeList = ({
  // disabledAttributes = [],
  scrollToSelected = true,
  height = 800,
  firstPage,
  setShowChartSide,
  activeDataSource = "common",
  onSearchChange,
}) => {
  const dispatch = useDispatch();

  // Redux selectors
  const axisPanelAtt = useSelector(
    (state) => state?.GraphConfig?.widgetGraphConfig || {}
  );
  const All_WM_attributes = useSelector(
    (state) => state.WidgetMaker.All_WM_attributes
  );
  const isshowSideBar = useSelector((state) => state.WidgetMaker.isshowSideBar);
  const authParams = useSelector((state) => state.Authentication.authParams);
  const chartLoader = useSelector((state) => state?.WidgetAPI?.chart_loader);
  const graphConditionAggregation = useSelector(
    (state) => state?.apiCall?.graphConditions?.column_aggregations
  );
  const graphConditions = useSelector(
    (state) => state?.apiCall?.graphConditions
  );
  const WM_attributes = useSelector((state) => state.WidgetMaker.WM_attributes);
  const graphInfoLoader = useSelector(
    (state) => state?.WidgetMaker?.graph_info_loader
  );
  const chartAttributesFieldConfig = useSelector(
    (state) => state?.WidgetMaker?.chart_attributes_field_config
  );
  const widgetMakerGraphData = useSelector(
    (state) => state?.WidgetAPI?.widget_maker_graph_data
  );

  // State management
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [activeKeys, setActiveKeys] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [highlightItem, setHighlightItem] = useState(false);
  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [favouriteAttributes, setFavouriteAttributes] = useState([]); // Store favourite attribute IDs
  const [confirmingUnfavourite, setConfirmingUnfavourite] = useState(null); // Track which attribute is being confirmed for unfavourite

  // Refs
  const selectedItemRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const resizeTimeoutRef = useRef(null);

  // Computed values
  const currentField = chartAttributesFieldConfig?.selected_field || "x_axis";
  const actualFieldClicked =
    chartAttributesFieldConfig?.actual_field_clicked || currentField;
  const isCardSettingsMode =
    chartAttributesFieldConfig?.open_chart_setting_flag;
  const selectedCardId = axisPanelAtt?.selected_card_id;

  // Function to get all attributes that are already selected in other fields
  const getDisabledAttributes = () => {
    const disabledAttrs = [];
    const chartType = axisPanelAtt?.chart_type;
    const currentField = chartAttributesFieldConfig?.selected_field;

    if (!currentField) return disabledAttrs;

    // Check if current field is a Quick Filter field
    const isQuickFilterField =
      currentField === "filter_dropdown" ||
      currentField.startsWith("filter_dropdown_");

    // If Quick Filter field is selected, only disable other Quick Filter attributes
    if (isQuickFilterField) {
      if (axisPanelAtt?.filter_dropdown?.length > 0) {
        const currentFilterNumber = currentField.startsWith("filter_dropdown_")
          ? parseInt(currentField.split("_")[2])
          : 0;

        // Add attributes from OTHER Quick Filter fields to disabled list
        axisPanelAtt.filter_dropdown.forEach((filterData, index) => {
          if (filterData?.attribute) {
            // Only disable if it's from a different Quick Filter field
            if (
              currentFilterNumber === 0 ||
              index + 1 !== currentFilterNumber
            ) {
              disabledAttrs.push(filterData.attribute);
            }
          }
        });
      }
      return disabledAttrs;
    }

    // For non-Quick Filter fields, apply original logic
    // Get selected attributes from Redux state
    const selectedAttributes = {};

    // Sync x_axis
    if (axisPanelAtt?.x_axis?.attribute) {
      selectedAttributes.x_axis = {
        attribute: axisPanelAtt.x_axis.attribute,
      };
    }

    // Sync breakdown_by
    if (axisPanelAtt?.breakdown_by?.length > 0) {
      // Handle multiple y_axes fields
      if (chartType === "grid" || chartType === "cluster-chart") {
        axisPanelAtt.breakdown_by.forEach((breakdown, index) => {
          selectedAttributes[`y_axes_${index + 1}`] = {
            attribute: breakdown.attribute,
          };
        });
      }

      // Also set the base breakdown field for backward compatibility
      const firstBreakdown = axisPanelAtt.breakdown_by?.[0];
      selectedAttributes.breakdown = {
        attribute: firstBreakdown.attribute,
      };
      selectedAttributes.y_axes = {
        attribute: firstBreakdown.attribute,
      };
    }

    // Sync filter_by
    if (
      axisPanelAtt?.filter_by?.length > 0 &&
      axisPanelAtt.filter_by[0]?.attribute
    ) {
      selectedAttributes.filter_by = {
        attribute: axisPanelAtt.filter_by[0].attribute,
      };
    }

    // Special handling for cluster-chart child nodes
    if (chartType === "cluster-chart") {
      // For Child Nodes 2 field, disable attributes selected in Child Nodes 1
      if (
        currentField === "y_axes_2" &&
        axisPanelAtt?.breakdown_by?.[0]?.attribute
      ) {
        disabledAttrs.push(axisPanelAtt.breakdown_by[0].attribute);
      }
      // For Child Nodes 1 field, disable attributes selected in Child Nodes 2
      else if (
        currentField === "y_axes_1" &&
        axisPanelAtt?.breakdown_by?.[1]?.attribute
      ) {
        disabledAttrs.push(axisPanelAtt.breakdown_by[1].attribute);
      }
      // Also disable Parent Node attribute in child nodes
      if (
        (currentField === "y_axes_1" || currentField === "y_axes_2") &&
        axisPanelAtt?.x_axis?.attribute
      ) {
        disabledAttrs.push(axisPanelAtt.x_axis.attribute);
      }
    }

    // Sync y_axis (y_series)
    if (axisPanelAtt?.y_axis && typeof axisPanelAtt.y_axis === "object") {
      Object.entries(axisPanelAtt.y_axis).forEach(([ySeriesKey, yAxisData]) => {
        if (yAxisData?.attribute) {
          selectedAttributes[ySeriesKey] = {
            attribute: yAxisData.attribute,
          };
        }
      });
    }

    // Build disabled attributes list
    Object.entries(selectedAttributes).forEach(([fieldKey, fieldData]) => {
      // Skip the current field
      if (fieldKey === currentField) return;

      // For map charts where currentField is "y_series", skip y_series_1
      // since that's the field being edited
      if (currentField === "y_series" && fieldKey === "y_series_1") return;

      // Skip breakdown field when viewing breakdown to avoid showing its own selection as disabled
      if (currentField === "breakdown" && fieldKey === "breakdown") {
        return;
      }

      // Skip y_axes field when viewing y_axes to avoid showing its own selection as disabled
      if (currentField === "y_axes" && fieldKey === "y_axes") {
        return;
      }

      // IMPORTANT: When Breakdown By field has payload_key_name="y_axes",
      // currentField is "y_axes" but we also need to skip "breakdown" entries
      if (currentField === "y_axes" && fieldKey === "breakdown") {
        return;
      }

      // Skip only the current y_axes field to avoid showing its own selection as disabled
      // But DO show other y_axes fields as disabled to prevent duplicate selections
      if (currentField?.startsWith("y_axes_") && fieldKey === currentField) {
        return;
      }

      // Also skip the generic y_axes/breakdown fields when viewing a specific y_axes_N field
      if (
        currentField?.startsWith("y_axes_") &&
        (fieldKey === "breakdown" || fieldKey === "y_axes")
      ) {
        return;
      }

      // Skip y_axes_N fields and generic y_axes field when viewing breakdown field
      // This prevents attributes selected in Group By fields from being disabled in breakdown view
      if (
        currentField === "breakdown" &&
        (fieldKey.startsWith("y_axes_") || fieldKey === "y_axes")
      ) {
        return;
      }

      // Chart types that allow duplicate attributes in y-series fields
      const allowDuplicateYSeriesChartTypes = [
        "combination",
        "bar",
        "horizontal-bar",
        "area",
        "line",
        "grid",
      ];

      // For y-series fields in specific chart types, allow duplicate attributes
      if (
        (currentField?.startsWith("y_series_") ||
          currentField === "y_series") &&
        fieldKey.startsWith("y_series_") &&
        allowDuplicateYSeriesChartTypes.includes(chartType)
      ) {
        // Allow same attribute to be used in multiple y-series for these chart types
        return;
      }

      // Special condition for heatmap chart: Allow same date attribute in both x_axis and breakdown_by
      if (chartType === "heatmap") {
        // Check if the current attribute being evaluated is a date attribute
        const isDateAttribute =
          fieldData?.attribute &&
          (fieldData.attribute.toLowerCase().includes("date") ||
            fieldData.attribute.toLowerCase().includes("time") ||
            fieldData.attribute?.toLowerCase() === "date" ||
            fieldData.attribute?.toLowerCase() === "createddate");

        // For heatmap, allow the same date attribute to be used in both x_axis and breakdown_by
        if (isDateAttribute) {
          // Allow date attribute in x_axis even if it's already in breakdown
          if (fieldKey === "x_axis" && currentField === "breakdown") {
            return; // Skip adding to disabled list
          }

          // Allow date attribute in breakdown even if it's already in x_axis
          if (fieldKey === "breakdown" && currentField === "x_axis") {
            return; // Skip adding to disabled list
          }

          // Also handle y_axes field for multi-level breakdowns
          if (fieldKey === "x_axis" && currentField === "y_axes") {
            return; // Skip adding to disabled list
          }

          if (fieldKey === "y_axes" && currentField === "x_axis") {
            return; // Skip adding to disabled list
          }
        }
      }

      // Handle both single and multiple selections
      if (Array.isArray(fieldData)) {
        fieldData.forEach((attr) => {
          disabledAttrs.push(attr.attribute);
        });
      } else if (fieldData) {
        disabledAttrs.push(fieldData.attribute);
      }
    });

    return disabledAttrs;
  };
  let disabledAttributes = getDisabledAttributes();

  // Helper functions
  const getAggregation = (attr) => {
    if (graphConditionAggregation?.avg?.includes(attr)) return "avg";
    if (graphConditionAggregation?.average_attribute?.includes(attr))
      return "avg";
    if (graphConditionAggregation?.count_distinct?.includes(attr))
      return "COUNT";
    if (graphConditionAggregation?.count?.includes(attr)) return "COUNT";
    if (graphConditionAggregation?.max?.includes(attr)) return "max";
    if (["nss", "srs"].includes(attr)) return "COUNT";
    return "SUM";
  };

  // Helper function to generate y_axis_settings based on y-series count
  const generateYAxisSettings = (yAxisData, chartType) => {
    const allowedChartTypes = [
      "combination",
      "horizontal-bar",
      "bar",
      "area",
      "line",
      "lollipop",
      "scatter",
      "quadrant",
      "multilayer-map",
      "treemap",
    ];
    if (!allowedChartTypes.includes(chartType)) return null;

    const ySeriesCount = Object.keys(yAxisData || {}).filter(
      (key) => key.startsWith("y_series_") && yAxisData[key]?.attribute
    ).length;

    if (ySeriesCount === 0) return null;

    // For multilayer-map, get the color settings
    let multilayerMapColorSetting = null;
    if (
      chartType === "multilayer-map" &&
      graphConditions?.["multilayer_map_color_setting"]
    ) {
      // Get the first y_series attribute to determine color settings
      const firstYSeriesKey = Object.keys(yAxisData || {}).find(
        (key) => key.startsWith("y_series_") && yAxisData[key]?.attribute
      );
      if (firstYSeriesKey && yAxisData[firstYSeriesKey]?.attribute) {
        multilayerMapColorSetting =
          graphConditions["multilayer_map_color_setting"][
            yAxisData[firstYSeriesKey].attribute
          ] || null;
      }
    }

    const settings = [];

    // First y-series always has show_as: "bar" for combination chart
    const firstSetting = {
      show_as: chartType === "combination" ? "bar" : "line",
      y_axis_map_at: 0,
      y_axis_scale: "linear",
      color_pattern_code: 1,
      key: uuid(),
    };

    // Add multilayer_thresholds for multilayer-map
    if (chartType === "multilayer-map") {
      firstSetting.multilayer_thresholds = multilayerMapColorSetting;
    }

    settings.push(firstSetting);

    // Additional y-series always have show_as: "line"
    for (let i = 1; i < ySeriesCount; i++) {
      settings.push({
        show_as: "line",
        y_axis_map_at: 1,
        show_markers: true,
        show_data_label: true,
        y_axis_scale: "linear",
        color_pattern_code: 1,
        key: uuid(),
        multilayer_thresholds:
          chartType === "multilayer-map" ? multilayerMapColorSetting : null,
      });
    }

    return settings;
  };

  // Helper function to determine if an attribute is a date/time type
  const isDateTimeAttribute = useCallback(
    (attributeName) => {
      return (
        graphConditions?.["date-attributes"]?.includes(attributeName) || false
      );
    },
    [graphConditions]
  );

  // Helper function to determine order_by logic for Grid and Pivot-Grid charts
  // This implements the order_by logic from the old Widget Maker's onDragEnd function
  // Conditions: result.destination.droppableId.split("#")[1] === "columns" (Grid)
  //           result.destination.droppableId.split("#")[1] === "columns_pivot" (Pivot-Grid)
  const determineOrderByForGridCharts = useCallback(
    (targetField, attribute, fieldName = null) => {
      const isGridChart = axisPanelAtt?.chart_type === "grid";
      const isPivotGridChart = axisPanelAtt?.chart_type === "pivot-grid";

      if (!isGridChart && !isPivotGridChart) {
        return null; // No order_by logic for non-grid charts
      }

      const isDateTimeAttrInXAxis = isDateTimeAttribute(
        axisPanelAtt?.x_axis?.attribute
      );
      const isCurrentAttrDateTime = isDateTimeAttribute(attribute?.attribute);

      switch (targetField) {
        case "x_axis":
          if (isCurrentAttrDateTime) {
            // For datetime x_axis, order by x_axis ascending
            return {
              attribute: attribute.attribute,
              data_source: axisPanelAtt?.data_source || "mentions",
              sort_order: "asc",
              no_of_rows: 10,
              offset: 0,
            };
          } else {
            //isDateTimeAttrInXAxis in this case this is dependent on the y_series 1 attribute
            // For non-datetime x_axis, order by y_series_1 if it exists
            const ySeriesAttr = axisPanelAtt?.y_axis?.y_series_1?.attribute;
            if (ySeriesAttr) {
              return {
                attribute: ySeriesAttr,
                data_source:
                  axisPanelAtt?.y_axis?.y_series_1?.data_source || "mentions",
                sort_order: "desc",
                no_of_rows: 10,
                offset: 0,
              };
            } else {
              // Fallback to the new attribute
              return {
                attribute: "tagid",
                data_source: axisPanelAtt?.data_source || "mentions",
                sort_order: "desc",
                no_of_rows: 10,
                offset: 0,
              };
            }
          }

        case "y_series":
          if (isDateTimeAttrInXAxis) {
            // If x_axis is datetime, keep order by x_axis ascending
            return {
              attribute: axisPanelAtt?.x_axis?.attribute,
              data_source: axisPanelAtt?.x_axis?.data_source || "mentions",
              sort_order: "asc",
              no_of_rows: 10,
              offset: 0,
            };
          } else {
            // For y_series_1 or single y_series, order by the new attribute
            if (
              fieldName === "y_series_1" ||
              fieldName === "y_series" ||
              !fieldName
            ) {
              return {
                attribute: attribute.attribute,
                data_source: axisPanelAtt?.data_source || "mentions",
                sort_order: "desc",
                no_of_rows: 10,
                offset: 0,
              };
            } else {
              // For other y_series (y_series_2, etc.), keep existing order_by if present
              if (axisPanelAtt?.order_by?.attribute) {
                return {
                  ...axisPanelAtt.order_by,
                  no_of_rows: 10,
                  offset: 0,
                };
              } else {
                // No existing order_by, set to the new attribute
                return {
                  attribute: attribute.attribute,
                  data_source: axisPanelAtt?.data_source || "mentions",
                  sort_order: "desc",
                  no_of_rows: 10,
                  offset: 0,
                };
              }
            }
          }

        case "breakdown_by":
          // For breakdown_by, order by the breakdown attribute

          if (isDateTimeAttrInXAxis) {
            // If x_axis is datetime, keep order by x_axis ascending
            return {
              attribute: axisPanelAtt?.x_axis?.attribute,
              data_source: axisPanelAtt?.x_axis?.data_source || "mentions",
              sort_order: "asc",
              no_of_rows: 10,
              offset: 0,
            };
          } else {
            //isDateTimeAttrInXAxis in this case this is dependent on the y_series 1 attribute
            // For non-datetime x_axis, order by y_series_1 if it exists
            const ySeriesAttr = axisPanelAtt?.y_axis?.y_series_1?.attribute;
            if (ySeriesAttr) {
              return {
                attribute: ySeriesAttr,
                data_source:
                  axisPanelAtt?.y_axis?.y_series_1?.data_source || "mentions",
                sort_order: "desc",
                no_of_rows: 10,
                offset: 0,
              };
            } else if (axisPanelAtt?.order_by?.attribute) {
              return {
                ...axisPanelAtt.order_by,
                no_of_rows: 10,
                offset: 0,
              };
            } else {
              return {
                attribute: attribute.attribute,
                data_source: axisPanelAtt?.data_source || "mentions",
                sort_order: "desc",
                no_of_rows: 10,
                offset: 0,
              };
            }
          }
        default:
          return null;
      }
    },
    [axisPanelAtt, graphConditions, isDateTimeAttribute]
  );

  // Helper function to check if order_by should be reset
  const shouldResetOrderBy = useCallback(
    (targetField, attribute, fieldName = null, currentOrderBy = null) => {
      if (!currentOrderBy?.attribute) return false;

      const isDateTimeAttrInXAxis = isDateTimeAttribute(
        axisPanelAtt?.x_axis?.attribute
      );
      const isCurrentAttrDateTime = isDateTimeAttribute(attribute?.attribute);

      switch (targetField) {
        case "x_axis":
          // Reset if we're updating the same x_axis attribute or it's datetime
          return (
            axisPanelAtt?.x_axis?.attribute === currentOrderBy.attribute ||
            isCurrentAttrDateTime ||
            isDateTimeAttrInXAxis
          );

        case "breakdown_by":
          // Reset if we're updating the same breakdown attribute
          if (fieldName && fieldName.startsWith("y_axes_")) {
            const axesNumber = parseInt(fieldName.split("_")[2]);
            return (
              axisPanelAtt?.breakdown_by?.[axesNumber - 1]?.attribute ===
              currentOrderBy.attribute
            );
          }
          return false;

        case "y_series":
          // Reset if we're updating the same y_series attribute
          if (fieldName) {
            const ySeriesAttribute =
              axisPanelAtt?.y_axis?.[fieldName]?.attribute;
            return ySeriesAttribute === currentOrderBy.attribute;
          }
          return false;

        default:
          return false;
      }
    },
    [axisPanelAtt, graphConditions, isDateTimeAttribute]
  );

  // function to send the default values in config values

  // loops over WM_attributes to find attribute and its data
  function findAttributeObject(attributeName, dataArray) {
    for (const innerArray of dataArray?.map((item) => item[1])) {
      if (Array.isArray(innerArray)) {
        const found = innerArray?.find(
          (item) => Array.isArray(item) && item[0] === attributeName
        );
        if (found) {
          return found[1]; // Return the matching attribute value
        }
      }
    }
    return null;
  }

  const setConfigFilters = (attribute, isSankeyXaxis) => {
    // to find attribute object based on attribute name looping on WM attributes
    let Extractvalues = findAttributeObject(attribute, WM_attributes);
    const initialColumnValues = (Extractvalues?.values || []).map((value) => ({
      name: value,
      type: "include",
      display_name: null,
    }));
    if (Extractvalues?.sub_values) {
      if (!isSankeyXaxis) {
        // runs for all cases except (sankey && x_axis)
        let defaultConfigFilter = [
          {
            attribute:
              Extractvalues?.sub_values?.end &&
              !axisPanelAtt?.breakdown_by?.some(
                (obj) => obj.attribute === attribute
              )
                ? Extractvalues?.sub_values?.end?.[0]?.attribute
                : Extractvalues?.sub_values?.start?.[0]?.attribute,
            data_source:
              Extractvalues?.sub_values?.end &&
              !axisPanelAtt?.breakdown_by?.some(
                (obj) => obj.attribute === attribute
              )
                ? Extractvalues?.sub_values?.end?.[0]?.data_source ||
                  Extractvalues?.sub_values?.end?.[0]?.source
                : Extractvalues?.sub_values?.start?.[0]?.data_source ||
                  Extractvalues?.sub_values?.start?.[0]?.source,
            trace_name:
              Extractvalues?.sub_values?.end &&
              !axisPanelAtt?.breakdown_by.some(
                (obj) => obj.attribute === attribute
              )
                ? Extractvalues?.sub_values?.end?.[0]?.trace_name
                : Extractvalues?.sub_values?.start?.[0]?.trace_name,
            type: "varchar",
            columns: initialColumnValues,
          },
        ];
        return defaultConfigFilter;
      } else {
        // case only for (sankey && x_axis)
        let defaultConfigFilterXaxis = [
          {
            attribute: Extractvalues?.sub_values?.start?.[0]?.attribute,
            data_source:
              Extractvalues?.sub_values?.start?.[0]?.data_source ||
              Extractvalues?.sub_values?.start?.[0]?.source,
            trace_name: Extractvalues?.sub_values?.start?.[0]?.trace_name,
            type: "varchar",
            columns: initialColumnValues,
          },
          {
            attribute: Extractvalues?.sub_values?.end?.[0]?.attribute,
            data_source:
              Extractvalues?.sub_values?.end?.[0]?.data_source ||
              Extractvalues?.sub_values?.end?.[0]?.source,
            trace_name: Extractvalues?.sub_values?.end?.[0]?.trace_name,
            type: "varchar",
            columns: initialColumnValues,
          },
        ];

        return defaultConfigFilterXaxis;
      }
    } else {
      return null;
    }
  };

  const createAttributeObject = useCallback(
    (attribute, options = {}) => {
      const {
        aggregation,
        sortOrder = DEFAULT_SORT_ORDER,
        limit = DEFAULT_LIMIT,
        includeFilters = true,
        additionalFields = {},
        isYSeries = false,
        isBreakdown = false,
        isMultilevelBreakdown = false,
        isQuickFilter = false,
        isXAxis = false,
      } = options;

      function checkAttributeSource(attributes, attName) {
        for (let key in attributes) {
          if (attributes?.hasOwnProperty(key)) {
            let attributeSection = attributes?.[key];
            let attributeContents = attributeSection?.[1];

            for (let i = 0; i < attributeContents?.length; i++) {
              let attribute = attributeContents?.[i];

              if (attribute?.[0] === attName) {
                return attribute?.[1]?.source;
              }
            }
          }
        }

        return false;
      }

      let attributes = { ...WM_attributes };
      const dragAttr = attribute.attribute;
      let newDataSource = checkAttributeSource(attributes, dragAttr);

      // Don't change data source for quick filter fields
      if (!isQuickFilter) {
        // If updating x-axis and y-series-1 has a data source, use y-series-1's data source
        const ySeries1DataSource =
          axisPanelAtt?.y_axis?.y_series_1?.data_source;
        if (isXAxis && ySeries1DataSource) {
          dispatch(
            setDataSource(
              attribute?.source ? attribute?.source : ySeries1DataSource
            )
          );
          // Also update the data source in the object being created
          newDataSource = ySeries1DataSource;
        } else {
          dispatch(
            setDataSource(attribute?.source ? attribute?.source : newDataSource)
          );
        }
      }

      let draggedAttributeObj = findAttributeObject(
        attribute?.attribute,
        WM_attributes
      );
      const baseObject = {
        attribute: attribute.attribute,
        trace_name: All_WM_attributes?.[attribute.attribute] || attribute.title,
        deep_dive_info: draggedAttributeObj?.deep_dive_info || null,
        data_source: newDataSource
          ? newDataSource
          : attribute?.source
          ? attribute?.source
          : attribute?.data_source
          ? attribute?.data_source
          : axisPanelAtt?.data_source || "mentions",
      };

      if (includeFilters) {
        baseObject.config_filters = setConfigFilters(attribute.attribute);
      }

      if (aggregation) {
        baseObject.aggregation = aggregation;
      }

      if (isMultilevelBreakdown) {
        baseObject.limit = 5;
      }

      // For breakdown/y_axes, add y_limit instead of x_limit and sort_order
      if (isBreakdown) {
        baseObject.y_limit = axisPanelAtt?.chart_type === "pivot-grid" ? 10 : 5;
      }
      // Don't add sort_order and x_limit for y_series or breakdown
      else if (sortOrder && !isYSeries && !isMultilevelBreakdown) {
        baseObject.sort_order = sortOrder;
        baseObject.x_limit =
          axisPanelAtt?.chart_type === "bar" ||
          axisPanelAtt?.chart_type === "horizontal-bar" ||
          axisPanelAtt?.chart_type === "lollipop" ||
          axisPanelAtt?.chart_type === "area" ||
          axisPanelAtt?.chart_type === "line" ||
          axisPanelAtt?.chart_type === "heatmap" ||
          axisPanelAtt?.chart_type === "scatter" ||
          axisPanelAtt?.chart_type === "quadrant" ||
          axisPanelAtt?.chart_type === "treemap"
            ? 30
            : axisPanelAtt?.chart_type === "pie" ||
              axisPanelAtt?.chart_type === "donut"
            ? 6
            : axisPanelAtt?.chart_type === "cluster-chart"
            ? 5
            : axisPanelAtt?.chart_type === "kpi" ||
              axisPanelAtt?.chart_type === "progress-kpi" ||
              axisPanelAtt?.chart_type === "sunburst"
            ? 10
            : axisPanelAtt?.chart_type === "bubble" ||
              axisPanelAtt?.chart_type === "wordcloud"
            ? 20
            : 50;
      }

      return { ...baseObject, ...additionalFields };
    },
    [All_WM_attributes, axisPanelAtt?.data_source, setConfigFilters]
  );

  const updateReduxState = useCallback(
    (updates) => {
      dispatch(
        setX_attribute({
          ...axisPanelAtt,
          ...updates,
        })
      );
    },
    [axisPanelAtt, chartAttributesFieldConfig, dispatch]
  );

  const updateLocalSelection = useCallback(
    (fieldName, attribute, isMultiple = false) => {
      setSelectedAttributes((prev) => {
        if (!isMultiple) {
          return { ...prev, [fieldName]: attribute };
        }

        const currentSelection = Array.isArray(prev[fieldName])
          ? prev[fieldName]
          : [];
        const isSelected = currentSelection.find(
          (attr) => attr.attribute === attribute.attribute
        );

        return {
          ...prev,
          [fieldName]: isSelected
            ? currentSelection.filter(
                (attr) => attr.attribute !== attribute.attribute
              )
            : [...currentSelection, attribute],
        };
      });
    },
    []
  );

  // Initialize favourite attributes from Redux state
  useEffect(() => {
    if (chartAttributesFieldConfig?.data) {
      const favouritesSet = new Set();

      // Check all fields for favourites
      chartAttributesFieldConfig.data.forEach((field) => {
        if (field.attributes) {
          const favourites =
            field.attributes.favourites || field.attributes.favorites;

          // Handle array format (new API format)
          if (Array.isArray(favourites)) {
            favourites.forEach((attrId) => favouritesSet.add(attrId));
          }
          // Handle object format (old format)
          else if (favourites && typeof favourites === "object") {
            const favouritesGroup = favourites["Favourites"];
            if (favouritesGroup && favouritesGroup.members) {
              Object.values(favouritesGroup.members).forEach((member) => {
                const attrId = member.attribute || member.key;
                if (attrId) favouritesSet.add(attrId);
              });
            }
          }
        }
      });

      setFavouriteAttributes(Array.from(favouritesSet));
    }
  }, [chartAttributesFieldConfig]);

  // Window resize handler
  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    const debounceResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(
        handleResize,
        RESIZE_DEBOUNCE_DELAY
      );
    };

    window.addEventListener("resize", debounceResize, { passive: true });

    return () => {
      window.removeEventListener("resize", debounceResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  // Search debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(modalSearchTerm);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [modalSearchTerm]);

  // Update parent component when sidebar state changes
  useEffect(() => {
    setShowChartSide?.(isshowSideBar);
  }, [isshowSideBar, setShowChartSide]);

  // Reset state when field changes
  useEffect(() => {
    setModalSearchTerm("");
    setDebouncedSearchTerm("");
    setIsInitialized(false);
    if (!isCardSettingsMode) {
      setActiveKeys([]);
    }
  }, [currentField, actualFieldClicked, isCardSettingsMode]);

  // Open all panels when activeDataSource (tab) changes (but keep search text)
  useEffect(() => {
    // Don't reset search terms - keep them when switching tabs
    // setModalSearchTerm("");
    // setDebouncedSearchTerm("");
    setIsInitialized(false);
    // Force re-initialization to open all panels
    setActiveKeys([]);
  }, [activeDataSource]);

  // Event handlers
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setModalSearchTerm(value);
      // Notify parent component about search change for tab switching
      if (onSearchChange) {
        onSearchChange(value);
      }
    },
    [onSearchChange]
  );

  const handleToggleSidebar = useCallback(() => {
    dispatch(setIsshowSideBar(!isshowSideBar));
  }, [dispatch, isshowSideBar]);

  const handlePanelChange = useCallback((keys) => {
    setActiveKeys(keys);
  }, []);

  // Favourite handlers
  const handleMarkFavourite = useCallback(
    (e, attribute, iconElement) => {
      e.stopPropagation(); // Prevent attribute selection when clicking star

      const attributeId = attribute.attribute || attribute.key;

      // Mark as favourite locally
      setFavouriteAttributes([...favouriteAttributes, attributeId]);

      // Play "save" sound effect - ascending pleasant chime
      try {
        const audioContext = new (window.AudioContext ||
          window.webkitAudioContext)();

        // Create a pleasant ascending two-note chime for "saving"
        const notes = [
          { freq: 523.25, gain: 0.3, start: 0, duration: 0.15 }, // C5
          { freq: 659.25, gain: 0.35, start: 0.1, duration: 0.25 }, // E5 (major third up)
        ];

        notes.forEach((note) => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();

          oscillator.type = "sine";
          oscillator.frequency.value = note.freq;

          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);

          // Quick attack, medium decay for a "success" feel
          gainNode.gain.setValueAtTime(
            0,
            audioContext.currentTime + note.start
          );
          gainNode.gain.linearRampToValueAtTime(
            note.gain,
            audioContext.currentTime + note.start + 0.01
          );
          gainNode.gain.exponentialRampToValueAtTime(
            0.01,
            audioContext.currentTime + note.start + note.duration
          );

          oscillator.start(audioContext.currentTime + note.start);
          oscillator.stop(
            audioContext.currentTime + note.start + note.duration
          );

          // Add a harmonic for richness
          const harmonic = audioContext.createOscillator();
          const harmonicGain = audioContext.createGain();

          harmonic.type = "sine";
          harmonic.frequency.value = note.freq * 2; // Octave up

          harmonic.connect(harmonicGain);
          harmonicGain.connect(audioContext.destination);

          harmonicGain.gain.setValueAtTime(
            0,
            audioContext.currentTime + note.start
          );
          harmonicGain.gain.linearRampToValueAtTime(
            note.gain * 0.15,
            audioContext.currentTime + note.start + 0.01
          );
          harmonicGain.gain.exponentialRampToValueAtTime(
            0.01,
            audioContext.currentTime + note.start + note.duration * 0.7
          );

          harmonic.start(audioContext.currentTime + note.start);
          harmonic.stop(audioContext.currentTime + note.start + note.duration);
        });
      } catch (err) {
        console.log("Audio creation failed:", err);
      }

      // Add animation to star icon
      if (iconElement) {
        // Since iconElement is already the .favourite-icon div, use it directly
        const starIcon = iconElement;

        // Add animation class for bounce effect
        starIcon.classList.add("animating");

        // Get the position of the star icon for sparkle placement
        const rect = starIcon.getBoundingClientRect();

        // Create sparkles container at the document body level to avoid overflow issues
        const sparklesContainer = document.createElement("div");
        sparklesContainer.style.position = "fixed";
        sparklesContainer.style.left = rect.left + rect.width / 2 + "px";
        sparklesContainer.style.top = rect.top + rect.height / 2 + "px";
        sparklesContainer.style.width = "1px";
        sparklesContainer.style.height = "1px";
        sparklesContainer.style.pointerEvents = "none";
        sparklesContainer.style.zIndex = "9999";

        // Create sparkles
        const sparkleCount = 8;
        const sparkleEmojis = ["⭐", "✨", "💫", "🌟"];

        for (let i = 0; i < sparkleCount; i++) {
          const sparkle = document.createElement("div");
          const angle = (((i * 360) / sparkleCount) * Math.PI) / 180;
          const distance = 30 + Math.random() * 20;

          sparkle.innerHTML =
            sparkleEmojis[Math.floor(Math.random() * sparkleEmojis.length)];
          sparkle.style.position = "absolute";
          sparkle.style.left = "0";
          sparkle.style.top = "0";
          sparkle.style.fontSize = 10 + Math.random() * 6 + "px";
          sparkle.style.transform = "translate(-50%, -50%)";
          sparkle.style.opacity = "1";
          sparkle.style.transition = "all 0.8s ease-out";

          sparklesContainer.appendChild(sparkle);

          // Animate after a brief delay
          setTimeout(() => {
            sparkle.style.transform = `translate(${
              Math.cos(angle) * distance
            }px, ${Math.sin(angle) * distance}px) scale(0)`;
            sparkle.style.opacity = "0";
          }, 50);
        }

        // Add sparkles to body
        document.body.appendChild(sparklesContainer);

        // Remove animation class and sparkles after animation completes
        setTimeout(() => {
          starIcon.classList.remove("animating");
          if (sparklesContainer.parentNode) {
            sparklesContainer.remove();
          }
        }, 1000);
      }

      // Update the Redux state to add to Favourites tab (globally for ALL fields)
      if (chartAttributesFieldConfig?.data) {
        const updatedData = [...chartAttributesFieldConfig.data];

        // Add favourites to ALL fields so they're visible everywhere
        updatedData.forEach((field, index) => {
          if (field.attributes) {
            // Check if favourites exist and their structure
            const existingFavourites =
              field.attributes.favourites || field.attributes.favorites;

            // Check if favourites is an array (new format from API)
            if (Array.isArray(existingFavourites)) {
              // If it's an array, append the new favorite attribute name if not already there
              if (!existingFavourites.includes(attributeId)) {
                field.attributes.favourites = [
                  ...existingFavourites,
                  attributeId,
                ];
              }
            } else if (
              existingFavourites &&
              typeof existingFavourites === "object"
            ) {
              // Handle old object format for backward compatibility
              // Initialize favorites if it doesn't exist OR if it's not in the expected structure
              if (
                !existingFavourites["Favourites"] ||
                (existingFavourites["Favourites"] &&
                  !existingFavourites["Favourites"].members)
              ) {
                // Preserve any existing data while ensuring proper structure
                field.attributes.favourites = {
                  Favourites: {
                    name: "Favourites",
                    members: existingFavourites?.["Favourites"]?.members || {},
                  },
                };
              }

              // Get the favorites group
              const favouritesGroup =
                field.attributes.favourites?.["Favourites"];

              if (favouritesGroup) {
                if (!favouritesGroup.members) {
                  favouritesGroup.members = {};
                }

                // Add the new favorite at the beginning (will appear at top)
                const newFavouriteKey = `fav_${Date.now()}_${attributeId}`;

                // Ensure we have all necessary fields for the favorite
                const favouriteData = {
                  title: attribute.title || attributeId,
                  attribute: attributeId,
                  key: attributeId,
                  datatype: attribute.datatype || "varchar",
                  source:
                    attribute.source ||
                    attribute.dataSource ||
                    activeDataSource,
                  description: attribute.description || "",
                  values: attribute.values || [],
                  id: attribute.id,
                  favourited_at: Date.now(),
                  original_field: chartAttributesFieldConfig.selected_field, // Track where it was favorited from
                  ...attribute, // Include any other fields from the original attribute
                };

                const newMembers = {
                  [newFavouriteKey]: favouriteData,
                };

                // Merge with existing members (new one first)
                favouritesGroup.members = {
                  ...newMembers,
                  ...favouritesGroup.members,
                };
              }
            } else {
              // If no favorites exist yet, initialize as array (new format)
              field.attributes.favourites = [attributeId];
            }
          }
        });

        // Update Redux state
        dispatch(
          setChartAttributesFieldConfig({
            ...chartAttributesFieldConfig,
            data: updatedData,
          })
        );
      }

      // TODO: Call API to mark as favorite
      // console.log("Marking as favorite:", attributeId);

      // API call to Save favorite
      let payloadObj = {
        authParams: authParams,
        attribute: attributeId,
        mode: true,
      };
      dispatch(setFavouritesAttributesAPI(payloadObj));
    },
    [favouriteAttributes, chartAttributesFieldConfig, dispatch]
  );

  const handleRemoveFavourite = useCallback(
    (attributeId) => {
      // Remove from favorites locally
      setFavouriteAttributes(
        favouriteAttributes.filter((id) => id !== attributeId)
      );
      // Reset confirmation state
      setConfirmingUnfavourite(null);

      // Play "remove" sound effect - descending gentle tone
      try {
        const audioContext = new (window.AudioContext ||
          window.webkitAudioContext)();

        // Create a gentle descending two-note sound for "removing"
        const notes = [
          { freq: 659.25, gain: 0.25, start: 0, duration: 0.12 }, // E5
          { freq: 493.88, gain: 0.2, start: 0.08, duration: 0.18 }, // B4 (fourth down)
        ];

        notes.forEach((note) => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();

          oscillator.type = "sine";
          oscillator.frequency.value = note.freq;

          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);

          // Softer attack and quicker decay for a "removal" feel
          gainNode.gain.setValueAtTime(
            0,
            audioContext.currentTime + note.start
          );
          gainNode.gain.linearRampToValueAtTime(
            note.gain,
            audioContext.currentTime + note.start + 0.02
          );
          gainNode.gain.exponentialRampToValueAtTime(
            0.01,
            audioContext.currentTime + note.start + note.duration
          );

          oscillator.start(audioContext.currentTime + note.start);
          oscillator.stop(
            audioContext.currentTime + note.start + note.duration
          );
        });

        // Add a subtle low-pass filtered swoosh for texture
        const swoosh = audioContext.createBufferSource();
        const swooshFilter = audioContext.createBiquadFilter();
        const swooshGain = audioContext.createGain();

        // Create filtered noise for swoosh
        const bufferSize = audioContext.sampleRate * 0.15;
        const buffer = audioContext.createBuffer(
          1,
          bufferSize,
          audioContext.sampleRate
        );
        const data = buffer.getChannelData(0);

        for (let i = 0; i < bufferSize; i++) {
          data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize); // Fade out
        }

        swoosh.buffer = buffer;

        // Low-pass filter for soft swoosh
        swooshFilter.type = "lowpass";
        swooshFilter.frequency.value = 1000;
        swooshFilter.Q.value = 0.5;

        swoosh.connect(swooshFilter);
        swooshFilter.connect(swooshGain);
        swooshGain.connect(audioContext.destination);

        swooshGain.gain.value = 0.05;
        swooshGain.gain.exponentialRampToValueAtTime(
          0.001,
          audioContext.currentTime + 0.15
        );

        swoosh.start(audioContext.currentTime);
        swoosh.stop(audioContext.currentTime + 0.15);
      } catch (err) {
        console.log("Audio creation failed:", err);
      }

      // Update the Redux state to remove from Favourites tab (from ALL fields)
      if (chartAttributesFieldConfig?.data) {
        const updatedData = [...chartAttributesFieldConfig.data];

        // Remove favorites from ALL fields
        updatedData.forEach((field) => {
          if (field.attributes) {
            const existingFavourites =
              field.attributes.favourites || field.attributes.favorites;

            // Check if favourites is an array (new format from API)
            if (Array.isArray(existingFavourites)) {
              // Remove the attribute from the array
              field.attributes.favourites = existingFavourites.filter(
                (id) => id !== attributeId
              );
            } else if (
              existingFavourites &&
              typeof existingFavourites === "object"
            ) {
              // Handle old object format for backward compatibility
              const favouritesGroup = existingFavourites["Favourites"];

              if (favouritesGroup && favouritesGroup.members) {
                // Remove the attribute from favorites
                const updatedMembers = {};
                Object.keys(favouritesGroup.members).forEach((key) => {
                  const member = favouritesGroup.members[key];
                  if ((member.attribute || member.key) !== attributeId) {
                    updatedMembers[key] = member;
                  }
                });
                favouritesGroup.members = updatedMembers;
              }
            }
          }
        });

        // Update Redux state
        dispatch(
          setChartAttributesFieldConfig({
            ...chartAttributesFieldConfig,
            data: updatedData,
          })
        );
      }

      // TODO: Call API to remove from favorites
      // console.log("Removing from favorites:", attributeId);

      // API call to Un Save favorite
      let payloadObj = {
        authParams: authParams,
        attribute: attributeId,
        mode: false,
      };
      dispatch(setFavouritesAttributesAPI(payloadObj));
    },
    [favouriteAttributes, chartAttributesFieldConfig, dispatch]
  );

  // Attribute selection handlers
  const handleCardSettings = useCallback(
    (attribute) => {
      const fieldType = currentField;

      const updatedCardSettings = axisPanelAtt?.card_settings?.map((card) => {
        if (card.card_id !== selectedCardId) return card;

        const baseCardUpdate = createAttributeObject(attribute, {
          aggregation: getAggregation(attribute?.attribute),
          sortOrder: fieldType === "x_axis" ? DEFAULT_SORT_ORDER : null,
          limit: fieldType === "x_axis" ? 5 : null,
          additionalFields:
            fieldType === "x_axis"
              ? {
                  date_part:
                    axisPanelAtt?.chart_type === "pie"
                      ? "ww"
                      : axisPanelAtt?.chart_type === "donut"
                      ? "ww"
                      : null,
                }
              : {},
        });

        const updatedCard = { ...card, [fieldType]: baseCardUpdate };

        // Add default y_series if x_axis is selected and y_series is missing
        if (fieldType === "x_axis" && !card.y_series?.attribute) {
          updatedCard.y_series = {
            attribute: "tagid",
            trace_name: "Mention Count",
            deep_dive_info: attribute.deep_dive_info || null,
            aggregation: getAggregation("tagid"),
            data_source: "mentions",
            // Don't add sort_order and x_limit for y_series
          };
        }

        return updatedCard;
      });

      updateReduxState({
        card_settings: updatedCardSettings,
        // chart_attributes_field_config: {
        //   ...chartAttributesFieldConfig,
        //   open_chart_setting_flag: true,
        // },
        selected_card_id: selectedCardId,
      });
      dispatch(
        setChartAttributesFieldConfig({
          ...chartAttributesFieldConfig,
          open_chart_setting_flag: true,
        })
      );

      updateLocalSelection(fieldType, attribute);
    },
    [
      axisPanelAtt,
      currentField,
      selectedCardId,
      createAttributeObject,
      updateReduxState,
      updateLocalSelection,
      chartAttributesFieldConfig,
    ]
  );

  const handleYSeries = useCallback(
    (attribute, fieldName) => {
      updateLocalSelection(fieldName, attribute);

      const updatedYAxis = {
        ...(axisPanelAtt?.y_axis || {}),
        [fieldName]: createAttributeObject(attribute, {
          aggregation: getAggregation(attribute?.attribute),
          isYSeries: true,
        }),
      };

      // Generate y_axis_settings based on updated y_axis
      const yAxisSettings = generateYAxisSettings(
        updatedYAxis,
        axisPanelAtt?.chart_type
      );

      const updates = { y_axis: updatedYAxis };
      if (yAxisSettings) {
        updates.y_axis_settings = yAxisSettings;
      }

      // Handle order_by logic for Grid and Pivot-Grid charts
      const currentOrderBy = axisPanelAtt?.order_by;
      const resetOrderBy = shouldResetOrderBy(
        "y_series",
        attribute,
        fieldName,
        currentOrderBy
      );
      const orderByForGrid = determineOrderByForGridCharts(
        "y_series",
        attribute,
        fieldName
      );

      // Dont update order in case of y-series updation
      // if (resetOrderBy || orderByForGrid) {
      //   updates.order_by = orderByForGrid;
      // }

      updateReduxState(updates);
    },
    [
      axisPanelAtt?.y_axis,
      axisPanelAtt?.chart_type,
      axisPanelAtt?.order_by,
      createAttributeObject,
      updateReduxState,
      updateLocalSelection,
      generateYAxisSettings,
      shouldResetOrderBy,
      determineOrderByForGridCharts,
    ]
  );

  const handleYAxes = useCallback(
    (attribute, fieldName) => {
      updateLocalSelection(fieldName, attribute);

      const axesNumber = parseInt(fieldName.split("_")[2]);
      const updatedBreakdownBy = [...(axisPanelAtt?.breakdown_by || [])];

      const additionalFields = {
        date_part:
          attribute?.attribute?.toLowerCase() === "createddate"
            ? "ww"
            : axisPanelAtt?.chart_type === "pie"
            ? "ww"
            : axisPanelAtt?.chart_type === "donut"
            ? "ww"
            : null,
      };

      // Add is_pivot: true for pivot-grid chart type
      if (axisPanelAtt?.chart_type === "pivot-grid") {
        additionalFields.is_pivot = true;
      }

      updatedBreakdownBy[axesNumber - 1] = createAttributeObject(attribute, {
        isBreakdown: true,
        additionalFields,
      });

      const updates = {
        breakdown_by: updatedBreakdownBy.filter(Boolean),
        // Reset postcard_pills_filter and filter_by_data when updating y_axes
        postcard_pills_filter: null,
        filter_by_data: null,
      };

      // For cluster-chart, reset deep_dive_on when updating y_axes
      if (
        axisPanelAtt?.chart_type === "cluster-chart" &&
        axisPanelAtt?.deep_dive_on
      ) {
        updates.deep_dive_on = null;
      }

      // Handle order_by logic for Grid and Pivot-Grid charts
      const currentOrderBy = axisPanelAtt?.order_by;
      const resetOrderBy = shouldResetOrderBy(
        "breakdown_by",
        attribute,
        fieldName,
        currentOrderBy
      );
      const orderByForGrid = determineOrderByForGridCharts(
        "breakdown_by",
        attribute,
        fieldName
      );

      if (resetOrderBy) {
        updates.order_by = orderByForGrid;
      }

      updateReduxState(updates);
    },
    [
      axisPanelAtt?.breakdown_by,
      axisPanelAtt?.chart_type,
      createAttributeObject,
      updateReduxState,
      updateLocalSelection,
    ]
  );

  // Helper function to check for gaps in y-series
  const hasYSeriesGaps = useCallback(() => {
    if (!axisPanelAtt?.y_axis) return false;

    // Find the highest y-series number that has an attribute
    let highestSeriesWithAttribute = 0;
    for (let i = 1; i <= 10; i++) {
      if (axisPanelAtt.y_axis[`y_series_${i}`]?.attribute) {
        highestSeriesWithAttribute = i;
      }
    }

    // Check for gaps - any missing series before the highest one
    for (let i = 1; i < highestSeriesWithAttribute; i++) {
      if (!axisPanelAtt.y_axis[`y_series_${i}`]?.attribute) {
        return true; // Found a gap
      }
    }

    return false;
  }, [axisPanelAtt?.y_axis]);

  const handleXAxis = useCallback(
    (attribute) => {
      // Check for y-series gaps before allowing x-axis change
      if (hasYSeriesGaps()) {
        callNotification(
          "Please add attribute to the asterisk(*) marked field(s)",
          "alert"
        );
        return;
      }
      updateLocalSelection("x_axis", attribute);

      const xAxisConfig = createAttributeObject(attribute, {
        isXAxis: true,
        additionalFields: {
          date_part:
            axisPanelAtt?.chart_type === "pie"
              ? "ww"
              : axisPanelAtt?.chart_type === "donut"
              ? "ww"
              : null,
        },
      });

      let updates = { x_axis: xAxisConfig };

      // For cluster-chart, reset deep_dive_on when updating x-axis
      if (
        axisPanelAtt?.chart_type === "cluster-chart" &&
        axisPanelAtt?.deep_dive_on
      ) {
        updates.deep_dive_on = null;
      }

      // Check if x-axis has tickets data source
      const xAxisDataSource =
        xAxisConfig.data_source ||
        attribute?.data_source ||
        axisPanelAtt?.data_source ||
        "mentions";

      // For scatter chart, add both y_series_1 and y_series_2 with default values
      if (axisPanelAtt?.chart_type === "scatter") {
        if (
          !axisPanelAtt?.y_axis?.y_series_1?.attribute ||
          !axisPanelAtt?.y_axis?.y_series_2?.attribute
        ) {
          // Set default y_series_1 to avg_rating
          if (!axisPanelAtt?.y_axis?.y_series_1?.attribute) {
            setSelectedAttributes((prev) => ({
              ...prev,
              y_series_1: {
                key: "avg_rating",
                title: "Average Review Ratings",
                attribute: "avg_rating",
                datatype: "float",
                source: "metrics",
                isGroupMember: false,
                values: [],
                description: "Average Review Ratings",
                data_source: "mentions",
              },
            }));
          }

          // Set default y_series_2 to tagid (Mention Count)
          if (!axisPanelAtt?.y_axis?.y_series_2?.attribute) {
            setSelectedAttributes((prev) => ({
              ...prev,
              y_series_2: {
                key: "tagid",
                title: "Mention Count",
                attribute: "tagid",
                datatype: "int",
                source: "metrics",
                isGroupMember: false,
                values: [],
                description: "Mention Count",
                data_source: "mentions",
              },
            }));
          }

          updates.y_axis = {
            ...axisPanelAtt?.y_axis,
            ...(!axisPanelAtt?.y_axis?.y_series_1?.attribute
              ? {
                  y_series_1: {
                    attribute: "avg_rating",
                    trace_name: "Average Review Ratings",
                    deep_dive_info: null,
                    data_source: "mentions",
                    config_filters: null,
                    aggregation: "SUM",
                  },
                }
              : {}),
            ...(!axisPanelAtt?.y_axis?.y_series_2?.attribute
              ? {
                  y_series_2: {
                    attribute: "tagid",
                    trace_name: "Mention Count",
                    deep_dive_info: null,
                    data_source: "mentions",
                    config_filters: null,
                    aggregation: "COUNT",
                  },
                }
              : {}),
          };
        }
      }
      // Add default y_series_1 if not exists for other chart types
      else if (!axisPanelAtt?.y_axis?.y_series_1?.attribute) {
        // If tickets data source, use NumberOfMentions with specific configuration
        if (xAxisDataSource === "tickets") {
          setSelectedAttributes((prev) => ({
            ...prev,
            y_series_1: {
              key: "number_of_mentions",
              title: "Ticket Mention Count",
              attribute: "NumberOfMentions",
              datatype: "int",
              source: "metrics",
              isGroupMember: false,
              values: [],
              description: "Count of ticket mentions",
              data_source: "tickets",
            },
          }));

          updates.y_axis = {
            ...axisPanelAtt?.y_axis,
            y_series_1: {
              attribute: "NumberOfMentions",
              trace_name: "Ticket Mention Count",
              deep_dive_info: null,
              data_source: "tickets",
              config_filters: null,
              aggregation: "SUM",
              // Don't add sort_order and x_limit for y_series
            },
          };
        } else {
          // Default mentions data source behavior
          setSelectedAttributes((prev) => ({
            ...prev,
            y_series_1: {
              key: "mention_count",
              title: "Mention Count",
              attribute: "tagid",
              datatype: "int",
              source: "metrics",
              isGroupMember: false,
              values: [],
              description: "Count of mentions",
              data_source: "mentions",
            },
          }));

          updates.y_axis = {
            ...axisPanelAtt?.y_axis,
            y_series_1: {
              attribute: "tagid",
              trace_name: "Mention Count",
              deep_dive_info: null,
              aggregation: getAggregation("tagid"),
              data_source: "mentions",
              // Don't add sort_order and x_limit for y_series
            },
          };
        }
      }

      // For combination chart, also add y_series_2 with NumLikesCount
      if (
        axisPanelAtt?.chart_type === "combination" &&
        !axisPanelAtt?.y_axis?.y_series_2?.attribute
      ) {
        setSelectedAttributes((prev) => ({
          ...prev,
          y_series_2: {
            key: "NumLikesCount",
            title: All_WM_attributes["NumLikesCount"] || "Number of Likes",
            attribute: "NumLikesCount",
            datatype: "int",
            source: "metrics",
            isGroupMember: false,
            values: [],
            description: "Number of likes count",
          },
        }));

        updates.y_axis = {
          ...(updates.y_axis || axisPanelAtt?.y_axis),
          y_series_2: {
            attribute: "NumLikesCount",
            aggregation: "SUM",
            trace_name: All_WM_attributes["NumLikesCount"] || "Number of Likes",
            data_source: xAxisConfig.data_source || "mentions",
            // Don't add sort_order and x_limit for y_series
          },
        };
      }

      // Add y_axis_settings based on y_axis data
      const yAxisSettings = generateYAxisSettings(
        updates.y_axis || axisPanelAtt?.y_axis,
        axisPanelAtt?.chart_type
      );
      if (yAxisSettings) {
        updates.y_axis_settings = yAxisSettings;
      }

      updateReduxState(updates);
    },
    [
      axisPanelAtt?.y_axis,
      axisPanelAtt?.chart_type,
      axisPanelAtt?.y_axis_settings,
      axisPanelAtt?.data_source,
      createAttributeObject,
      updateReduxState,
      updateLocalSelection,
      getAggregation,
      All_WM_attributes,
      generateYAxisSettings,
      hasYSeriesGaps,
      callNotification,
    ]
  );

  const handleOrderBy = useCallback(
    (attribute) => {
      updateLocalSelection("order_by", attribute);

      const orderByConfig = createAttributeObject(attribute, {
        sortOrder: "desc",
        limit: null,
      });

      updateReduxState({ order_by: orderByConfig });
    },
    [createAttributeObject, updateReduxState, updateLocalSelection]
  );

  const handleMultilevelBreakdown = useCallback(
    (attribute) => {
      updateLocalSelection("multilevel_breakdown", attribute);

      const multilevelBreakdownConfig = createAttributeObject(attribute, {
        isBreakdown: false,
        isMultilevelBreakdown: true,
        additionalFields: {
          date_part: null,
        },
      });

      const updates = {
        ...axisPanelAtt,
        multilevel_breakdown: multilevelBreakdownConfig,
      };

      // For cluster-chart, reset deep_dive_on when updating multilevel_breakdown
      if (
        axisPanelAtt?.chart_type === "cluster-chart" &&
        axisPanelAtt?.deep_dive_on
      ) {
        updates.deep_dive_on = null;
      }

      dispatch(setX_attribute(updates));
    },
    [
      axisPanelAtt,
      createAttributeObject,
      dispatch,
      updateLocalSelection,
      chartAttributesFieldConfig,
    ]
  );

  const handleFilterBy = useCallback(
    (attribute) => {
      updateLocalSelection("filter_by", attribute);

      // For filter_by, we need to create an object similar to breakdown but with different properties
      const filterByConfig = {
        attribute: attribute.attribute,
        trace_name: All_WM_attributes?.[attribute.attribute] || attribute.title,
        data_source:
          attribute?.source ||
          attribute?.data_source ||
          axisPanelAtt?.data_source ||
          "mentions",
        is_pivot: true,
        date_part: null,
        sort_order: "desc",
        y_limit: 5,
      };

      updateReduxState({
        filter_by: [filterByConfig],
        postcard_pills_filter: null,
        filter_by_data: null,
        breakdown_by: [],
      });
    },
    [
      All_WM_attributes,
      axisPanelAtt?.data_source,
      updateReduxState,
      updateLocalSelection,
    ]
  );

  const handleFilterDropdown = useCallback(
    (attribute, fieldName, allowsMultiple = false) => {
      const createFilterObject = () =>
        createAttributeObject(attribute, {
          sortOrder: DEFAULT_SORT_ORDER,
          limit: DEFAULT_FILTER_LIMIT,
          isQuickFilter: true,
          additionalFields: {
            date_part:
              axisPanelAtt?.chart_type === "pie"
                ? "ww"
                : axisPanelAtt?.chart_type === "donut"
                ? "ww"
                : null,
            date_aggregation: null,
            frequency: null,
            span_on: false,
            allow_multiple_selection: true,
          },
        });

      if (fieldName.startsWith("filter_dropdown_")) {
        const filterNumber = parseInt(fieldName.split("_")[2]);
        const updatedFilters = [...(axisPanelAtt?.filter_dropdown || [])];

        while (updatedFilters.length < filterNumber) {
          updatedFilters.push(null);
        }
        let filterObj = createFilterObject();
        if ("x_limit" in filterObj) {
          delete filterObj.x_limit; // Remove x_limit for filter_dropdown
        }
        updatedFilters[filterNumber - 1] = filterObj;
        updateReduxState({
          filter_dropdown: updatedFilters.filter(Boolean),
          filter_by_dropdown: [],
          filter_by_dropdown_data: [],
        });
        return;
      }

      // Handle base filter_dropdown
      const currentFilters = axisPanelAtt?.filter_dropdown || [];
      const isAlreadySelected = currentFilters.some(
        (f) => f.attribute === attribute.attribute
      );

      if (!isAlreadySelected) {
        let updatedFilters;
        if (allowsMultiple) {
          // For multiple selection, append to existing filters
          updatedFilters = [...currentFilters, createFilterObject()];
        } else {
          // For single selection, replace existing filters
          updatedFilters = [createFilterObject()];
        }

        updateReduxState({
          filter_dropdown: updatedFilters,
          filter_by_dropdown: [],
          filter_by_dropdown_data: [],
        });
      }
    },
    [
      axisPanelAtt?.filter_dropdown,
      axisPanelAtt?.chart_type,
      createAttributeObject,
      updateReduxState,
    ]
  );

  const handleBreakdown = useCallback(
    (attribute, fieldName, allowsMultiple) => {
      // Check for y-series gaps before allowing breakdown change
      if (hasYSeriesGaps()) {
        callNotification(
          "Please add attribute to the asterisk(*) marked field(s)",
          "alert"
        );
        return;
      }

      const additionalFields = {
        date_part:
          attribute?.attribute?.toLowerCase() === "createddate"
            ? "ww"
            : axisPanelAtt?.chart_type === "pie"
            ? "ww"
            : axisPanelAtt?.chart_type === "donut"
            ? "ww"
            : null,
      };

      // Add is_pivot: true for pivot-grid chart type
      if (axisPanelAtt?.chart_type === "pivot-grid") {
        additionalFields.is_pivot = true;
      }

      const breakdownObject = createAttributeObject(attribute, {
        isBreakdown: true,
        additionalFields,
      });

      if (fieldName === "y_axes" && allowsMultiple) {
        updateLocalSelection("y_axes", attribute, true);

        const currentBreakdownBy = axisPanelAtt?.breakdown_by || [];
        const isAlreadySelected = currentBreakdownBy.some(
          (item) => item.attribute === attribute.attribute
        );

        const updatedBreakdownBy = isAlreadySelected
          ? currentBreakdownBy.filter(
              (item) => item.attribute !== attribute.attribute
            )
          : [...currentBreakdownBy, breakdownObject];

        updateReduxState({
          breakdown_by: updatedBreakdownBy,
          // Reset postcard_pills_filter and filter_by_data when updating y_axes
          postcard_pills_filter: null,
          filter_by_data: null,
        });
      } else {
        updateLocalSelection(fieldName, attribute);
        updateReduxState({
          breakdown_by: [breakdownObject],
          // Reset postcard_pills_filter and filter_by_data when updating y_axes
          postcard_pills_filter: null,
          filter_by_data: null,
        });
      }
    },
    [
      axisPanelAtt?.breakdown_by,
      createAttributeObject,
      updateReduxState,
      updateLocalSelection,
    ]
  );

  // Check if attribute is selected
  const isAttributeSelected = useCallback(
    (attributeName) => {
      if (!currentField) return false;

      // Handle card settings mode
      if (isCardSettingsMode && selectedCardId) {
        const card = axisPanelAtt?.card_settings?.find(
          (c) => c.card_id === selectedCardId
        );
        if (card) {
          if (currentField === "x_axis")
            return card.x_axis?.attribute === attributeName;
          if (currentField === "y_series")
            return card.y_series?.attribute === attributeName;
        }
        return false;
      }

      // Check local state first
      const localSelected = selectedAttributes[currentField];
      if (localSelected) {
        if (Array.isArray(localSelected)) {
          return localSelected.some((attr) => attr.attribute === attributeName);
        }
        return localSelected.attribute === attributeName;
      }

      // Check Redux state
      if (!axisPanelAtt) return false;

      // Handle different field types
      if (currentField === "x_axis") {
        return axisPanelAtt.x_axis?.attribute === attributeName;
      }

      if (currentField === "order_by") {
        return axisPanelAtt.order_by?.attribute === attributeName;
      }

      if (currentField === "multilevel_breakdown") {
        return axisPanelAtt.multilevel_breakdown?.attribute === attributeName;
      }

      if (
        (currentField === "breakdown" || currentField === "y_axes") &&
        axisPanelAtt.breakdown_by?.length > 0
      ) {
        return axisPanelAtt.breakdown_by.some(
          (breakdown) => breakdown.attribute === attributeName
        );
      }

      if (currentField.startsWith("y_axes_")) {
        const axesNumber = parseInt(currentField.split("_")[2]);
        return (
          axisPanelAtt.breakdown_by?.[axesNumber - 1]?.attribute ===
          attributeName
        );
      }

      if (currentField.startsWith("y_series_")) {
        return axisPanelAtt.y_axis?.[currentField]?.attribute === attributeName;
      }

      if (
        currentField === "filter_dropdown" ||
        currentField.startsWith("filter_dropdown_")
      ) {
        if (axisPanelAtt.filter_dropdown?.length > 0) {
          if (currentField.startsWith("filter_dropdown_")) {
            const filterNumber = parseInt(currentField.split("_")[2]);
            return (
              axisPanelAtt.filter_dropdown[filterNumber - 1]?.attribute ===
              attributeName
            );
          }
          return axisPanelAtt.filter_dropdown.some(
            (filterData) => filterData.attribute === attributeName
          );
        }
      }

      if (currentField === "filter_by") {
        return (
          axisPanelAtt.filter_by?.length > 0 &&
          axisPanelAtt.filter_by[0]?.attribute === attributeName
        );
      }

      return false;
    },
    [
      axisPanelAtt,
      currentField,
      selectedAttributes,
      isCardSettingsMode,
      selectedCardId,
      chartAttributesFieldConfig,
    ]
  );

  const onAttributeSelect = useCallback(
    (attribute) => {
      if (!attribute) return;

      // Check if the attribute is already selected
      if (isAttributeSelected(attribute.attribute)) {
        // Don't do anything if the attribute is already selected
        return;
      }

      // Handle card settings mode
      if (isCardSettingsMode && selectedCardId) {
        handleCardSettings(attribute);
        return;
      }

      if (!currentField || !chartAttributesFieldConfig?.data) return;

      // Handle specific field types
      if (actualFieldClicked.startsWith("y_series_")) {
        handleYSeries(attribute, actualFieldClicked);
        return;
      }

      if (actualFieldClicked.startsWith("y_axes_")) {
        // condition for show total rows in gird only x_axis and y_series data must be present
        if (
          axisPanelAtt?.chart_type === "grid" &&
          axisPanelAtt?.chart_settings?.total_row
        ) {
          dispatch(setWidgetTotalRow(false, axisPanelAtt));
        }
        handleYAxes(attribute, actualFieldClicked);
        return;
      }

      // Get field configuration
      const getFieldConfig = () => {
        const baseConfig = chartAttributesFieldConfig?.data?.find(
          (field) => field.payload_key_name === currentField
        );

        if (!baseConfig && currentField.startsWith("filter_dropdown_")) {
          return chartAttributesFieldConfig?.data?.find(
            (field) => field.payload_key_name === "filter_dropdown"
          );
        }

        return baseConfig;
      };

      const fieldConfig = getFieldConfig();
      if (!fieldConfig) return;

      const allowsMultiple = fieldConfig.multiple_allowed || false;

      // Handle fields based on type
      switch (currentField) {
        case "x_axis":
          handleXAxis(attribute);
          break;

        case "order_by":
          handleOrderBy(attribute);
          break;

        case "filter_by":
          handleFilterBy(attribute);
          break;

        case "filter_dropdown":
          handleFilterDropdown(attribute, currentField, allowsMultiple);
          break;

        case "y_series":
          updateLocalSelection("y_series", attribute, allowsMultiple);
          // For y_series, we need to create the object without sort_order and x_limit
          const ySeries = createAttributeObject(attribute, {
            aggregation: getAggregation(attribute?.attribute),
            isYSeries: true,
          });

          // For single y_series field (not y_series_1, y_series_2, etc)
          // We need to update y_axis with y_series_1
          const updatedYAxis = {
            ...(axisPanelAtt?.y_axis || {}),
            y_series_1: ySeries,
          };

          // Generate y_axis_settings based on updated y_axis
          const yAxisSettings = generateYAxisSettings(
            updatedYAxis,
            axisPanelAtt?.chart_type
          );

          const updates = {
            y_series: ySeries,
            y_axis: updatedYAxis,
          };

          if (yAxisSettings) {
            updates.y_axis_settings = yAxisSettings;
          }

          updateReduxState(updates);
          break;

        case "y_axes":
        case "breakdown":
          handleBreakdown(attribute, currentField, allowsMultiple);
          break;

        case "multilevel_breakdown":
          handleMultilevelBreakdown(attribute);
          break;

        default:
          if (currentField.startsWith("filter_dropdown_")) {
            handleFilterDropdown(attribute, currentField, allowsMultiple);
          } else {
            updateLocalSelection(currentField, attribute, allowsMultiple);
          }
      }
    },
    [
      axisPanelAtt,
      actualFieldClicked,
      currentField,
      isCardSettingsMode,
      selectedCardId,
      isAttributeSelected,
      handleCardSettings,
      handleYSeries,
      handleYAxes,
      handleFilterDropdown,
      handleXAxis,
      handleOrderBy,
      handleBreakdown,
      handleMultilevelBreakdown,
      updateLocalSelection,
      createAttributeObject,
      getAggregation,
      generateYAxisSettings,
      updateReduxState,
      chartAttributesFieldConfig,
    ]
  );

  // Transform attributes for display
  const transformAttributesForField = useCallback(
    (fieldConfig, dataSource = null) => {
      if (!fieldConfig?.attributes) return [];

      const transformedAttributes = [];
      let attributesToProcess = fieldConfig.attributes;

      // If a specific data source is selected, filter to only that source
      if (dataSource) {
        // Special handling for pages tab - check both 'page' and 'pages' keys
        if (dataSource === "pages") {
          const pageData =
            attributesToProcess.page || attributesToProcess.pages;
          if (pageData) {
            attributesToProcess = { pages: pageData };
          } else {
            return [];
          }
        }
        // Special handling for favorites tab - check both 'favorites' and 'favourites' keys
        else if (dataSource === "favourites") {
          const favouritesData =
            attributesToProcess.favorites || attributesToProcess.favourites;

          if (favouritesData) {
            // If it's an array (list of favorite attribute names), we need to handle it differently
            if (Array.isArray(favouritesData)) {
              // Find the actual attribute objects from other data sources
              const favouriteAttributeObjects = [];

              // Search through all data sources to find the attribute objects
              favouritesData.forEach((attrName) => {
                // Search in all available data sources
                const sources = [
                  "common",
                  "author",
                  "mentions",
                  "tickets",
                  "page",
                  "pages",
                ];

                for (const source of sources) {
                  const sourceData = attributesToProcess[source];
                  if (sourceData) {
                    // Search through groups in this source
                    Object.entries(sourceData).forEach(
                      ([groupKey, groupData]) => {
                        if (groupData && groupData.members) {
                          // Check if attribute exists in this group's members
                          Object.entries(groupData.members).forEach(
                            ([memberKey, memberData]) => {
                              if (memberKey === attrName) {
                                favouriteAttributeObjects.push({
                                  key: memberKey,
                                  title:
                                    memberData.title ||
                                    memberKey
                                      .replace(/_/g, " ")
                                      .replace(/\b\w/g, (l) => l.toUpperCase()),
                                  id: memberData.id || groupData.id,
                                  attribute:
                                    memberData.attribute ||
                                    memberData.key ||
                                    memberKey,
                                  datatype:
                                    memberData.datatype ||
                                    fieldConfig.attribute_type ||
                                    "varchar",
                                  source: memberData.source || source,
                                  groupName: groupKey
                                    .replace(/_/g, " ")
                                    .replace(/\b\w/g, (l) => l.toUpperCase()),
                                  isGroupMember: true,
                                  groupId: groupData.id,
                                  values: memberData.values || [],
                                  description: memberData.description,
                                  dataSource: "favorites",
                                  isFavourite: true,
                                  originalSource: source,
                                  ...memberData,
                                });
                              }
                            }
                          );
                        } else if (groupData && groupKey === attrName) {
                          // Handle non-grouped attributes
                          favouriteAttributeObjects.push({
                            key: groupKey,
                            title:
                              groupData.title ||
                              groupKey
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (l) => l.toUpperCase()),
                            id: groupData.id,
                            attribute: groupKey,
                            datatype:
                              groupData.datatype ||
                              fieldConfig.attribute_type ||
                              "varchar",
                            source: groupData.source || source,
                            isGroupMember: false,
                            values: groupData.values || [],
                            description: groupData.description,
                            dataSource: "favorites",
                            isFavourite: true,
                            originalSource: source,
                            ...groupData,
                          });
                        }
                      }
                    );
                  }
                }
              });

              // Remove duplicates based on attribute key
              const uniqueFavorites = [];
              const seenAttributes = new Set();

              favouriteAttributeObjects.forEach((favObj) => {
                const attrKey = favObj.attribute || favObj.key;
                if (!seenAttributes.has(attrKey.toLowerCase())) {
                  seenAttributes.add(attrKey.toLowerCase());
                  uniqueFavorites.push(favObj);
                }
              });

              return uniqueFavorites;
            }
            // Use the correct key that exists in the data
            const favouritesKey = attributesToProcess.favourites
              ? "favourites"
              : "favorites";
            attributesToProcess = { [favouritesKey]: favouritesData };
          } else {
            return [];
          }
        } else if (attributesToProcess[dataSource]) {
          attributesToProcess = {
            [dataSource]: attributesToProcess[dataSource],
          };
        } else {
          // If data source doesn't exist in attributes, return empty
          return [];
        }
      }

      // Process each data source (common, mentions, tickets, page, favorites/favourites)
      Object.entries(attributesToProcess).forEach(([sourceKey, sourceData]) => {
        if (!sourceData) return;

        // Process groups within each data source
        Object.entries(sourceData).forEach(([groupKey, groupData]) => {
          if (groupData && groupData.members) {
            // Handle grouped attributes
            Object.entries(groupData.members).forEach(
              ([memberKey, memberData]) => {
                transformedAttributes.push({
                  key: memberKey,
                  title:
                    memberData.title ||
                    memberKey
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase()),
                  id: memberData.id || groupData.id,
                  attribute:
                    memberData.attribute || memberData.key || memberKey,
                  datatype:
                    memberData.datatype ||
                    fieldConfig.attribute_type ||
                    "varchar",
                  source: memberData.source || sourceKey,
                  groupName: groupKey
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase()),
                  isGroupMember: true,
                  groupId: groupData.id,
                  values: memberData.values || [],
                  description: memberData.description,
                  dataSource:
                    sourceKey === "favourites" ? "favorites" : sourceKey, // Normalize to 'favorites'
                  ...memberData,
                });
              }
            );
          } else if (groupData && !groupData.members) {
            // Handle non-grouped attributes
            transformedAttributes.push({
              key: groupKey,
              title:
                groupData.title ||
                groupKey
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase()),
              id: groupData.id,
              attribute: groupKey,
              datatype:
                groupData.datatype || fieldConfig.attribute_type || "varchar",
              source: groupData.source || sourceKey,
              isGroupMember: false,
              values: groupData.values || [],
              description: groupData.description,
              dataSource: sourceKey, // Track which data source this comes from
              ...groupData,
            });
          }
        });
      });

      return transformedAttributes;
    },
    [activeDataSource]
  );

  const getCurrentFieldAttributesForModal = useCallback(() => {
    // Handle card settings mode
    if (isCardSettingsMode && selectedCardId) {
      const fieldType = currentField;
      if (chartAttributesFieldConfig?.data) {
        const fieldConfig = chartAttributesFieldConfig.data.find(
          (field) => field.payload_key_name === fieldType
        );

        if (fieldConfig?.attributes) {
          return transformAttributesForField(fieldConfig, activeDataSource);
        }
      }
      return [];
    }

    // Handle regular mode
    if (!chartAttributesFieldConfig?.data || !currentField) {
      return [];
    }

    // Find appropriate field config
    let fieldConfig;

    if (currentField.startsWith("y_series_")) {
      fieldConfig = chartAttributesFieldConfig?.data?.find(
        (field) => field.payload_key_name === "y_series"
      );

      // Special handling for scatter and multilayer-map chart types - restrict attributes for each y_series
      if (
        (axisPanelAtt?.chart_type === "scatter" ||
          axisPanelAtt?.chart_type === "multilayer-map") &&
        fieldConfig
      ) {
        const allAttributes = transformAttributesForField(
          fieldConfig,
          activeDataSource
        );

        if (axisPanelAtt?.chart_type === "scatter") {
          if (currentField === "y_series_1") {
            // Scatter Y-series 1: Only allow specific rating/score attributes
            return allAttributes.filter((attr) =>
              graphConditions[
                axisPanelAtt?.chart_type
              ]?.primary_series?.include?.includes(attr.attribute)
            );
          } else if (currentField === "y_series_2") {
            // Scatter Y-series 2: Only allow count attributes
            return allAttributes.filter((attr) =>
              graphConditions[
                axisPanelAtt?.chart_type
              ]?.secondary_series?.include?.includes(attr.attribute)
            );
          }
        } else if (axisPanelAtt?.chart_type === "multilayer-map") {
          if (currentField === "y_series_1") {
            // multilayer-map Y-series 1: Only allow count attributes
            return allAttributes.filter((attr) =>
              graphConditions[
                axisPanelAtt?.chart_type
              ]?.primary_series?.include?.includes(attr.attribute)
            );
          } else if (currentField === "y_series_2") {
            // multilayer-map Y-series 2: Only allow rating attributes
            return allAttributes.filter((attr) =>
              graphConditions[
                axisPanelAtt?.chart_type
              ]?.secondary_series?.include?.includes(attr.attribute)
            );
          }
        }
      }

      // Special condition for y_series_2: If y_series_1 has time-label attribute, filter to only show time-label attributes
      // Exception: For combination chart type, allow all attributes
      if (
        currentField === "y_series_2" &&
        fieldConfig &&
        axisPanelAtt?.chart_type !== "combination"
      ) {
        const y_series_1_attr = axisPanelAtt?.y_axis?.y_series_1?.attribute;

        if (
          y_series_1_attr &&
          (graphConditions?.["time-label-attrs"]?.includes(y_series_1_attr) ||
            graphConditions?.["ticket-time-label-attrs"]?.includes(
              y_series_1_attr
            ))
        ) {
          // Transform attributes first
          const allAttributes = transformAttributesForField(
            fieldConfig,
            activeDataSource
          );

          // Filter to only show time-label attributes
          return allAttributes.filter(
            (attr) =>
              graphConditions?.["time-label-attrs"]?.includes(attr.attribute) ||
              graphConditions?.["ticket-time-label-attrs"]?.includes(
                attr.attribute
              )
          );
        }
      }
    } else if (currentField.startsWith("y_axes_")) {
      fieldConfig = chartAttributesFieldConfig?.data?.find(
        (field) => field.payload_key_name === "y_axes"
      );
    } else if (currentField.startsWith("filter_dropdown_")) {
      fieldConfig = chartAttributesFieldConfig?.data?.find(
        (field) => field.payload_key_name === "filter_dropdown"
      );
    } else {
      fieldConfig = chartAttributesFieldConfig?.data?.find(
        (field) => field.payload_key_name === currentField
      );
    }

    // Special handling for heatmap x_axis when breakdown_by has date attribute
    if (
      currentField === "x_axis" &&
      axisPanelAtt?.chart_type === "heatmap" &&
      fieldConfig
    ) {
      // Check if breakdown_by has any date attribute
      const breakdownByAttr = axisPanelAtt?.breakdown_by?.[0]?.attribute;

      if (
        breakdownByAttr &&
        (breakdownByAttr.toLowerCase().includes("date") ||
          breakdownByAttr?.toLowerCase() === "date" ||
          breakdownByAttr?.toLowerCase() === "createddate" ||
          breakdownByAttr.toLowerCase().includes("time"))
      ) {
        // Get the regular x_axis attributes
        const xAxisAttributes = transformAttributesForField(
          fieldConfig,
          activeDataSource
        );

        // Find and add all date/timing attributes from all field configs
        const allFieldConfigs = chartAttributesFieldConfig?.data || [];
        const dateAttributes = [];

        // Collect date/timing attributes from all available field configurations
        allFieldConfigs.forEach((field) => {
          // Handle pages/page inconsistency
          let sourceData = field.attributes?.[activeDataSource];
          if (!sourceData && activeDataSource === "pages") {
            sourceData = field.attributes?.page;
          }

          if (sourceData) {
            Object.entries(sourceData).forEach(([groupKey, groupData]) => {
              // Check if this is a timing/date group
              if (
                groupKey.toLowerCase().includes("timing") ||
                groupKey.toLowerCase().includes("date") ||
                groupKey === "Timing" ||
                groupKey === "ADS Campaign Attributes"
              ) {
                // Add all attributes from date/timing groups
                if (groupData.is_group && groupData.members) {
                  Object.entries(groupData.members).forEach(
                    ([memberKey, memberData]) => {
                      if (
                        !dateAttributes.find(
                          (attr) => attr.attribute === memberKey
                        )
                      ) {
                        dateAttributes.push({
                          key: memberKey,
                          title:
                            memberData.title ||
                            memberKey
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase()),
                          id: memberData.id || groupData.id,
                          attribute: memberKey,
                          datatype: field.attribute_type || "varchar",
                          source: groupKey,
                          groupName: groupKey
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (l) => l.toUpperCase()),
                          isGroupMember: true,
                          groupId: groupData.id,
                          values: memberData.values || [],
                          description: memberData.description,
                          ...memberData,
                        });
                      }
                    }
                  );
                } else if (!groupData.is_group) {
                  // Single attribute in date/timing group
                  const attrKey = groupData.attribute || groupKey;
                  if (
                    !dateAttributes.find((attr) => attr.attribute === attrKey)
                  ) {
                    dateAttributes.push({
                      key: attrKey,
                      title:
                        groupData.title ||
                        attrKey
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase()),
                      id: groupData.id,
                      attribute: attrKey,
                      datatype: field.attribute_type || "varchar",
                      source: groupKey,
                      groupName: groupKey
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase()),
                      isGroupMember: false,
                      values: groupData.values || [],
                      description: groupData.description,
                      ...groupData,
                    });
                  }
                }
              }
            });
          }
        });

        // Merge x_axis attributes with date attributes (without duplicates)
        const mergedAttributes = [...xAxisAttributes];
        dateAttributes.forEach((dateAttr) => {
          if (
            !mergedAttributes.find(
              (attr) => attr.attribute === dateAttr.attribute
            )
          ) {
            mergedAttributes.push(dateAttr);
          }
        });

        return mergedAttributes;
      }
    }

    return fieldConfig
      ? transformAttributesForField(fieldConfig, activeDataSource)
      : [];
  }, [
    axisPanelAtt,
    currentField,
    isCardSettingsMode,
    selectedCardId,
    transformAttributesForField,
    graphConditions,
    chartAttributesFieldConfig,
    activeDataSource,
  ]);

  const getFilteredAttributesForModal = useCallback(() => {
    const currentAttributes = getCurrentFieldAttributesForModal();
    if (!debouncedSearchTerm) return currentAttributes;

    const searchTerm = debouncedSearchTerm.toLowerCase();
    return currentAttributes.filter(
      (attr) =>
        attr.title?.toLowerCase().includes(searchTerm) ||
        attr.source?.toLowerCase().includes(searchTerm) ||
        attr.datatype?.toLowerCase().includes(searchTerm) ||
        attr.attribute?.toLowerCase().includes(searchTerm) ||
        attr.groupName?.toLowerCase().includes(searchTerm) ||
        attr.description?.toLowerCase().includes(searchTerm)
    );
  }, [getCurrentFieldAttributesForModal, debouncedSearchTerm]);

  const attributes = useMemo(
    () => getFilteredAttributesForModal(),
    [getFilteredAttributesForModal]
  );

  // Group attributes for display
  const groupedAttributes = useMemo(() => {
    if (!attributes || !Array.isArray(attributes)) return {};

    const grouped = {};

    attributes.forEach((attr) => {
      const groupName = attr.groupName || attr.source || "Other";

      if (!grouped[groupName]) {
        grouped[groupName] = {
          title: groupName,
          attributes: [],
        };
      }

      grouped[groupName].attributes.push(attr);
    });

    return grouped;
  }, [attributes]);

  // Get selected attribute for current field
  const getSelectedAttributeForCurrentField = useCallback(() => {
    if (isCardSettingsMode && selectedCardId) {
      const card = axisPanelAtt?.card_settings?.find(
        (c) => c.card_id === selectedCardId
      );
      if (card) {
        if (currentField === "x_axis") return card.x_axis?.attribute;
        if (currentField === "y_series") return card.y_series?.attribute;
      }
      return null;
    }

    // Handle different field types
    if (currentField === "x_axis") {
      return axisPanelAtt?.x_axis?.attribute;
    }

    if (currentField === "order_by") {
      return axisPanelAtt?.order_by?.attribute;
    }

    if (currentField === "multilevel_breakdown") {
      return axisPanelAtt?.multilevel_breakdown?.attribute;
    }

    if (
      (currentField === "breakdown" || currentField === "y_axes") &&
      axisPanelAtt?.breakdown_by?.length > 0
    ) {
      return axisPanelAtt.breakdown_by?.[0]?.attribute;
    }

    if (currentField.startsWith("y_axes_")) {
      const axesNumber = parseInt(currentField.split("_")[2]);
      return axisPanelAtt?.breakdown_by?.[axesNumber - 1]?.attribute;
    }

    if (currentField.startsWith("y_series_")) {
      return axisPanelAtt?.y_axis?.[currentField]?.attribute;
    }

    if (
      (currentField === "filter_dropdown" ||
        currentField.startsWith("filter_dropdown_")) &&
      axisPanelAtt?.filter_dropdown?.length > 0
    ) {
      if (currentField.startsWith("filter_dropdown_")) {
        const filterNumber = parseInt(currentField.split("_")[2]);
        return axisPanelAtt.filter_dropdown[filterNumber - 1]?.attribute;
      }
      return axisPanelAtt.filter_dropdown[0]?.attribute;
    }

    if (currentField === "filter_by") {
      return axisPanelAtt?.filter_by?.length > 0
        ? axisPanelAtt.filter_by[0]?.attribute
        : null;
    }

    return null;
  }, [
    axisPanelAtt,
    currentField,
    isCardSettingsMode,
    selectedCardId,
    chartAttributesFieldConfig,
  ]);

  // Sync selected attributes with Redux state
  useEffect(() => {
    const getSelectedAttributesFromState = () => {
      const selected = {};

      if (!axisPanelAtt) return selected;

      // Handle card settings flow
      if (isCardSettingsMode && selectedCardId) {
        const card = axisPanelAtt?.card_settings?.find(
          (c) => c.card_id === selectedCardId
        );
        if (card) {
          if (card.x_axis?.attribute) {
            selected.x_axis = { attribute: card.x_axis.attribute };
          }
          if (card.y_series?.attribute) {
            selected.y_series = { attribute: card.y_series.attribute };
          }
        }
        return selected;
      }

      // Regular flow
      if (axisPanelAtt.x_axis?.attribute) {
        selected.x_axis = { attribute: axisPanelAtt.x_axis.attribute };
      }

      if (axisPanelAtt.breakdown_by?.length > 0) {
        selected.breakdown = axisPanelAtt.breakdown_by.map((breakdown) => ({
          attribute: breakdown.attribute,
        }));
        selected.y_axes = selected.breakdown;
      }

      if (axisPanelAtt.y_axis && typeof axisPanelAtt.y_axis === "object") {
        Object.entries(axisPanelAtt.y_axis).forEach(([fieldName, yAxis]) => {
          if (yAxis?.attribute) {
            selected[fieldName] = { attribute: yAxis.attribute };
          }
        });
      }

      if (axisPanelAtt.order_by?.attribute) {
        selected.order_by = { attribute: axisPanelAtt.order_by.attribute };
      }

      if (axisPanelAtt.filter_dropdown?.length > 0) {
        selected.filter_dropdown = axisPanelAtt.filter_dropdown.map(
          (filterData) => ({ attribute: filterData.attribute })
        );
      }

      if (
        axisPanelAtt.filter_by?.length > 0 &&
        axisPanelAtt.filter_by[0]?.attribute
      ) {
        selected.filter_by = { attribute: axisPanelAtt.filter_by[0].attribute };
      }

      if (axisPanelAtt.multilevel_breakdown?.attribute) {
        selected.multilevel_breakdown = {
          attribute: axisPanelAtt.multilevel_breakdown.attribute,
        };
      }

      return selected;
    };

    setSelectedAttributes(getSelectedAttributesFromState());
  }, [
    axisPanelAtt,
    isCardSettingsMode,
    selectedCardId,
    chartAttributesFieldConfig,
  ]);

  // Initialize panels - always open all panels on initialization or tab change
  useEffect(() => {
    if (!isInitialized && Object.keys(groupedAttributes).length > 0) {
      const allKeys = Object.keys(groupedAttributes);
      // Always open all panels
      setActiveKeys(allKeys);
      setIsInitialized(true);
    }
  }, [
    groupedAttributes,
    isInitialized,
    activeDataSource, // Re-trigger when tab changes
  ]);

  // Scroll to selected attribute
  useEffect(() => {
    const selectedAttribute = getSelectedAttributeForCurrentField();

    if (scrollToSelected && selectedAttribute && isInitialized) {
      setTimeout(() => {
        if (selectedItemRef.current) {
          const scrollContainer =
            selectedItemRef.current.closest(".custom_scroll1") ||
            selectedItemRef.current.closest(".ant-layout-content");

          if (scrollContainer) {
            const itemRect = selectedItemRef.current.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            const scrollTop =
              scrollContainer.scrollTop +
              itemRect.top -
              containerRect.top -
              containerRect.height / 2 +
              itemRect.height / 2;

            scrollContainer.scrollTo({
              top: scrollTop,
              behavior: "smooth",
            });
          } else {
            selectedItemRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "nearest",
            });
          }

          setHighlightItem(true);
          setTimeout(() => setHighlightItem(false), HIGHLIGHT_DURATION);
        }
      }, SCROLL_ANIMATION_DELAY);
    }
  }, [scrollToSelected, isInitialized, getSelectedAttributeForCurrentField]);

  // Compute disabled attributes for Quick Filter
  const computeDisabledAttributesForQuickFilter = useCallback(() => {
    const disabled = [...disabledAttributes];

    if (
      currentField &&
      (currentField === "filter_dropdown" ||
        currentField.startsWith("filter_dropdown_"))
    ) {
      const currentFilterNumber = currentField.startsWith("filter_dropdown_")
        ? parseInt(currentField.split("_")[2])
        : 0;

      if (axisPanelAtt?.filter_dropdown?.length > 0) {
        axisPanelAtt.filter_dropdown.forEach((filterData, index) => {
          if (currentFilterNumber === 0 || index + 1 !== currentFilterNumber) {
            if (
              filterData?.attribute &&
              !disabled.includes(filterData.attribute)
            ) {
              disabled.push(filterData.attribute);
            }
          }
        });
      }
    }

    return disabled;
  }, [currentField, axisPanelAtt?.filter_dropdown, disabledAttributes]);

  // Get field title
  const getFieldTitle = useCallback(() => {
    if (!chartAttributesFieldConfig?.data || !currentField) {
      return "";
    }

    // Card Settings Mode
    if (
      chartAttributesFieldConfig?.open_chart_setting_flag &&
      currentField === "x_axis"
    ) {
      return "Group By";
    }
    if (
      chartAttributesFieldConfig?.open_chart_setting_flag &&
      currentField === "y_series"
    ) {
      return "Value";
    }

    if (currentField.startsWith("y_series_")) {
      const seriesNumber = currentField.split("_")[2];
      return `Y-series ${seriesNumber}`;
    }

    if (currentField.startsWith("y_axes_")) {
      const axesNumber = currentField.split("_")[2];
      return `Group By ${axesNumber}`;
    }

    if (currentField === "multilevel_breakdown") {
      return "Breakdown By";
    }

    let fieldConfig = chartAttributesFieldConfig?.data?.find(
      (field) => field.payload_key_name === currentField
    );

    if (!fieldConfig && currentField.startsWith("filter_dropdown_")) {
      fieldConfig = chartAttributesFieldConfig?.data?.find(
        (field) => field.payload_key_name === "filter_dropdown"
      );
    }

    return fieldConfig ? fieldConfig.field_name : "";
  }, [chartAttributesFieldConfig?.data, currentField]);

  // Render functions
  const renderAttribute = useCallback(
    (attr) => {
      const isSelected = isAttributeSelected(attr.attribute);
      const effectiveDisabledAttributes =
        computeDisabledAttributesForQuickFilter();

      // Special handling for favorites tab - filter based on field type
      let isDisabled = false;

      if (activeDataSource === "favourites") {
        // For order_by field, never disable favorites attributes
        if (currentField === "order_by") {
          isDisabled = false;
        } else {
          // For other fields, keep the original disable logic
          // First check if the attribute is in the disabled list (selected in other fields)
          if (
            disabledAttributes.includes(attr.attribute) ||
            effectiveDisabledAttributes.includes(attr.attribute)
          ) {
            isDisabled = true;
          } else {
            // If not already disabled, then check datatype compatibility
            const isNumericAttribute =
              attr.datatype &&
              (attr.datatype.toLowerCase() === "int" ||
                attr.datatype.toLowerCase() === "integer" ||
                attr.datatype.toLowerCase() === "float" ||
                attr.datatype.toLowerCase() === "double" ||
                attr.datatype.toLowerCase() === "decimal" ||
                attr.datatype.toLowerCase() === "number");

            const isVarcharAttribute =
              attr.datatype &&
              (attr.datatype.toLowerCase() === "varchar" ||
                attr.datatype.toLowerCase() === "string" ||
                attr.datatype.toLowerCase() === "text" ||
                attr.datatype.toLowerCase() === "char");

            // Check if it's a datetime attribute by datatype or common datetime attribute names
            const isDateTimeAttribute =
              (attr.datatype &&
                (attr.datatype.toLowerCase() === "datetime" ||
                  attr.datatype.toLowerCase() === "date" ||
                  attr.datatype.toLowerCase() === "timestamp" ||
                  attr.datatype.toLowerCase() === "time")) ||
              (attr.attribute &&
                (attr.attribute.toLowerCase().includes("date") ||
                  attr.attribute.toLowerCase().includes("time") ||
                  attr.attribute.toLowerCase().includes("created") ||
                  attr.attribute.toLowerCase().includes("updated") ||
                  attr.attribute.toLowerCase().includes("modified") ||
                  attr.attribute?.toLowerCase() === "date" ||
                  attr.attribute?.toLowerCase() === "createddate" ||
                  attr.attribute === "Ticket Created Date"));

            // For x-axis: allow varchar/string and datetime attributes
            if (currentField === "x_axis") {
              // X-axis can accept categorical or datetime data
              isDisabled = !isVarcharAttribute && !isDateTimeAttribute;
            }
            // For breakdown and y_axes: only allow varchar/string attributes
            else if (
              currentField === "breakdown" ||
              currentField === "multilevel_breakdown" ||
              currentField?.startsWith("y_axes_")
            ) {
              // These fields typically need categorical/varchar data
              isDisabled = !isVarcharAttribute;
            }
            // For y-series: only allow numeric attributes
            else if (
              currentField?.startsWith("y_series") ||
              currentField === "y_series"
            ) {
              // These fields need numeric data for aggregation
              isDisabled = !isNumericAttribute;
            }
            // For filter fields: allow all types
            else if (currentField?.startsWith("filter_dropdown")) {
              isDisabled = false;
            }
            // For other fields, check if already selected
            else {
              isDisabled = effectiveDisabledAttributes.includes(attr.attribute);
            }
          }
        }
      } else {
        // For non-favorites tabs, use the normal disabled logic
        isDisabled = isCardSettingsMode
          ? !widgetMakerGraphData
          : effectiveDisabledAttributes.includes(attr.attribute);
      }

      // Prepare tooltip for disabled items
      let disabledTooltip = "";
      if (isDisabled && activeDataSource === "favourites") {
        // Check if disabled because it's already selected in another field
        if (
          disabledAttributes.includes(attr.attribute) ||
          effectiveDisabledAttributes.includes(attr.attribute)
        ) {
          disabledTooltip = `"${
            attr.title || attr.attribute
          }" is already selected in another field`;
        }
        // Otherwise it's disabled due to datatype mismatch
        else if (currentField === "x_axis") {
          disabledTooltip = `This field requires text/categorical or datetime attributes. "${
            attr.title || attr.attribute
          }" is a numeric attribute.`;
        } else if (
          currentField === "breakdown" ||
          currentField === "multilevel_breakdown" ||
          currentField?.startsWith("y_axes_")
        ) {
          disabledTooltip = `This field requires text/categorical attributes. "${
            attr.title || attr.attribute
          }" is a numeric attribute.`;
        } else if (
          currentField?.startsWith("y_series") ||
          currentField === "y_series"
        ) {
          disabledTooltip = `This field requires numeric attributes. "${
            attr.title || attr.attribute
          }" is a text attribute.`;
        }
      } else if (isDisabled) {
        // For non-favorites tabs
        disabledTooltip = `"${
          attr.title || attr.attribute
        }" is already selected in another field`;
      }

      const attributeContent = (
        <div
          key={attr.key || attr.attribute}
          ref={isSelected ? selectedItemRef : null}
          className={`attribute-list-item ${isSelected ? "selected" : ""} ${
            isDisabled ? "disabled" : ""
          } ${isSelected && highlightItem ? "highlight" : ""}`}
          onClick={() =>
            !isDisabled && onAttributeSelect && onAttributeSelect(attr)
          }
        >
          <div className="attribute-info">
            <Text
              strong
              className={`attribute-name ${isDisabled ? "disabled-text" : ""}`}
            >
              {attr.title}
            </Text>
            {attr.description && attr.description !== attr.title && (
              <Text
                type="secondary"
                className={`attribute-description ${
                  isDisabled ? "disabled-text" : ""
                }`}
              >
                {attr?.description === "NULL" ? "" : attr?.description}
              </Text>
            )}
          </div>
          <div className="attribute-right">
            {/* Favorite star icon with inline confirmation */}
            {favouriteAttributes.includes(attr.attribute || attr.key) ? (
              // If this attribute is currently being confirmed for unfavorite
              confirmingUnfavourite === (attr.attribute || attr.key) ? (
                <div className="favourite-confirmation">
                  <Tooltip title="Remove from favorites">
                    <div
                      className="confirm-icon confirm-yes"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFavourite(attr.attribute || attr.key);
                        setConfirmingUnfavourite(null);
                      }}
                    >
                      <CheckOutlined
                        style={{ color: "#52c41a", fontSize: "12px" }}
                      />
                    </div>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <div
                      className="confirm-icon confirm-no"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmingUnfavourite(null);
                      }}
                    >
                      <CloseOutlined
                        style={{ color: "#ff4d4f", fontSize: "12px" }}
                      />
                    </div>
                  </Tooltip>
                </div>
              ) : (
                // Show filled star that can be clicked to start unfavorite confirmation
                <Tooltip title="Click to remove from favorites">
                  <div
                    className="favourite-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmingUnfavourite(attr.attribute || attr.key);
                    }}
                  >
                    <StarFilled
                      style={{ color: "#faad14", fontSize: "16px" }}
                    />
                  </div>
                </Tooltip>
              )
            ) : (
              <Tooltip title="Mark as favorite">
                <div
                  className="favourite-icon"
                  onClick={(e) => handleMarkFavourite(e, attr, e.currentTarget)}
                >
                  <StarOutlined
                    style={{ color: "#8c8c8c", fontSize: "16px" }}
                  />
                </div>
              </Tooltip>
            )}

            {/* Check icon for selected items only (not for disabled) */}
            {isCardSettingsMode && !widgetMakerGraphData ? (
              ""
            ) : (
              <>
                {isSelected && !isDisabled && (
                  <div className="check-icon">
                    <CheckOutlined />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      );

      if (isDisabled) {
        const tooltipTitle =
          disabledTooltip ||
          (isCardSettingsMode && !widgetMakerGraphData
            ? "Graph data not available"
            : "Already selected");
        return (
          <Tooltip
            title={tooltipTitle}
            placement="top"
            key={attr.key || attr.attribute}
          >
            {attributeContent}
          </Tooltip>
        );
      }

      return attributeContent;
    },
    [
      isAttributeSelected,
      computeDisabledAttributesForQuickFilter,
      isCardSettingsMode,
      widgetMakerGraphData,
      highlightItem,
      onAttributeSelect,
      activeDataSource,
      currentField,
    ]
  );

  const customExpandIcon = useCallback(
    ({ isActive }) => (
      <div className="expand-icon-wrapper">
        {isActive ? <DownOutlined /> : <RightOutlined />}
      </div>
    ),
    []
  );

  // Computed styles
  const contentStyle = useMemo(() => {
    const dynamicMinHeight = Math.floor(viewportHeight * 0.6); // adjust multiplier as needed
    const baseHeight = firstPage ? viewportHeight - 255 : viewportHeight - 205;

    return {
      height: `${Math.max(baseHeight, dynamicMinHeight)}px`,
      overflow: "auto",
      minHeight: `${dynamicMinHeight}px`,
      maxHeight: `${Math.min(
        viewportHeight * VIEWPORT_HEIGHT_MULTIPLIER,
        MAX_HEIGHT_LIMIT
      )}px`,
    };
  }, [firstPage, viewportHeight]);

  const isDarkTheme = authParams?.theme_type === "1";
  const themeClass = isDarkTheme ? "darkTheme" : "lightTheme";

  return (
    <div className="widget-maker-attribute-list-unique">
      <Layout className={`${chartLoader ? "disable" : ""} ${themeClass}`}>
        <Header
          className={`h-auto p-15 line-height-normal wm-attribute-header ${themeClass}`}
        >
          <div className="d-flex justify-content-between mb-2 align-items-center">
            <Text
              className={`text_heading font-weight600 attribute-list-title current-field ${
                isshowSideBar ? "d-none" : ""
              } ${themeClass}`}
            >
              {getFieldTitle()} Attributes
            </Text>
            <Text
              className="text_heading font-weight600 cursor-pointer"
              onClick={handleToggleSidebar}
            >
              <img
                src={
                  authParams?.theme_type === "1"
                    ? toggleArrowWhite
                    : toggleArrow
                }
                alt="toggleArrow"
                style={{
                  transform: isshowSideBar ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              />
            </Text>
          </div>
          <div
            // style={{ marginTop: "20px" }}
            className={`${
              isshowSideBar ? "d-none" : "d-flex"
            } justify-content-between align-items-center`}
          >
            <Input
              placeholder="Search attributes..."
              prefix={<SearchOutlined />}
              value={modalSearchTerm}
              onChange={handleSearchChange}
              className={`search-input ${themeClass}`}
            />
          </div>
        </Header>
        <Content
          className={`custom_scroll1 ${
            isshowSideBar ? "d-none" : ""
          } ${themeClass}`}
          style={contentStyle}
        >
          {graphInfoLoader ? (
            <div className="attribute-list-skeleton">
              {/* Skeleton for collapsed panels */}
              {[1, 2].map((index) => (
                <div key={index} style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      padding: "12px 16px",
                      backgroundColor: isDarkTheme ? "#2a2a2a" : "#f5f5f5",
                      borderRadius: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <Skeleton.Input
                      active
                      size="small"
                      style={{ width: "150px" }}
                    />
                  </div>
                  {/* Skeleton for expanded items */}
                  {index === 1 && (
                    <div style={{ padding: "0 16px" }}>
                      {[1, 2, 3, 4].map((itemIndex) => (
                        <div
                          key={itemIndex}
                          style={{
                            padding: "12px 16px",
                            borderBottom: isDarkTheme
                              ? "1px solid #333"
                              : "1px solid #f0f0f0",
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                          }}
                        >
                          <Skeleton.Input
                            active
                            size="small"
                            style={{ width: "60%" }}
                          />
                          <Skeleton.Input
                            active
                            size="small"
                            style={{ width: "80%", height: "16px" }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {index === 2 && (
                    <div style={{ padding: "0 16px" }}>
                      {[1, 2, 3].map((itemIndex) => (
                        <div
                          key={itemIndex}
                          style={{
                            padding: "12px 16px",
                            borderBottom: isDarkTheme
                              ? "1px solid #333"
                              : "1px solid #f0f0f0",
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                          }}
                        >
                          <Skeleton.Input
                            active
                            size="small"
                            style={{ width: "60%" }}
                          />
                          <Skeleton.Input
                            active
                            size="small"
                            style={{ width: "80%", height: "16px" }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : Object.keys(groupedAttributes).length === 0 ? (
            <div className="empty-attributes">
              No attributes found for this field type.
            </div>
          ) : activeDataSource === "favourites" ? (
            // For favorites tab, show flat list without collapse panels
            <div className="favourites-flat-list">
              {Object.values(groupedAttributes).flatMap((group) =>
                group.attributes.map((attr) => renderAttribute(attr))
              )}
            </div>
          ) : (
            <Collapse
              activeKey={activeKeys}
              onChange={handlePanelChange}
              expandIcon={customExpandIcon}
              expandIconPosition="start"
              className={`attribute-collapse ${themeClass}`}
            >
              {Object.entries(groupedAttributes).map(([groupKey, group]) => (
                <Panel
                  key={groupKey}
                  header={
                    <div className="panel-header">
                      <span className="panel-title">
                        <Tooltip title={group.title} placement="top">
                          {group.title?.length > 20
                            ? group.title?.substring(0, 20) + "..."
                            : group.title}
                        </Tooltip>
                      </span>
                    </div>
                  }
                  className="attribute-panel"
                >
                  <div className="attribute-list">
                    {group.attributes.map((attr) => renderAttribute(attr))}
                  </div>
                </Panel>
              ))}
            </Collapse>
          )}
        </Content>
      </Layout>
    </div>
  );
};

export default React.memo(AttributeList);
